import { Controller } from '@hotwired/stimulus';

// The DAM page (/admin/dam). Design: docs/design/flow-designs/one-thing.html.
// Home = ONE thing at a time (next TikTok, cooldown, receipt); Plan, Library
// and Results sit behind the Everything menu.
// All data comes from /api/v1/dam; this controller only draws and calls it.

const PL = ['tiktok', 'youtube', 'facebook', 'instagram', 'linkedin', 'x'];
const CLAUDE = PL.filter((k) => k !== 'tiktok');
const LABEL = { tiktok: 'TikTok', youtube: 'YouTube', facebook: 'Facebook', instagram: 'Instagram', linkedin: 'LinkedIn', x: 'X/Twitter' };
const COLOR = { tiktok: 'hsl(340 82% 52%)', youtube: 'hsl(0 80% 52%)', facebook: 'hsl(221 83% 53%)', instagram: 'hsl(291 64% 52%)', linkedin: 'hsl(201 100% 35%)', x: 'hsl(222 47% 11%)' };
const LIMIT = { tiktok: 4000, youtube: 5000, facebook: 63206, instagram: 2200, linkedin: 3000, x: 280 };
const KINDS = ['video', 'hook', 'image', 'asset'];
const CADENCES = [['queue', 'Add to the end, 1 a day'], ['1d', '1 a day from today'], ['2d', 'Every 2 days'], ['3h', 'In 3 hours'], ['tonight', 'Tonight 10 PM']];
const STATE_LABEL = { planned: 'Planned', scheduled: 'Scheduled', posted: 'Posted', missed: 'Missed', queued: 'Queued', not_live: 'Not live yet' };
// YouTube has no API: Claude unschedules it on its daily 9 AM run. Booked before that, Andrew does it in Studio.
const YT_WAIT = 'YouTube changes when Claude runs next (9 AM). Booked sooner? Set it Private in Studio now.';
const DAY = 864e5;
const COOLDOWN = 3 * 36e5;
const PLAN_START = -7;
const PLAN_END = 30;
// Platforms paused for now (none since Meta cleared the account on 2026-10-11); never shown as handled.
const PAUSED = [];
// Where an item may go (Dam::Item::LANES).
const LANES = [['all', 'Everywhere'], ['no_youtube', 'Not YouTube'], ['tiktok_first', 'TikTok first, then Facebook']];
const RING = 2 * Math.PI * 86;
const ET = 'America/New_York';
const etHour = (d) => +new Intl.DateTimeFormat('en-US', { timeZone: ET, hour: 'numeric', hourCycle: 'h23' }).format(d);
const etLabel = (h) => `${h % 12 || 12} ${h >= 12 ? 'PM' : 'AM'}`;
// Claude's slots are US Eastern; say so wherever a time is shown next to Andrew's own.
const fmtET = (d) => `${new Intl.DateTimeFormat('en-US', { timeZone: ET, weekday: 'short', hour: 'numeric', minute: '2-digit' }).format(d).replace(':00', '')} ET`;

const BTN = 'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0';
const BTN_OUTLINE = `${BTN} h-10 border border-input bg-background px-4 hover:bg-accent hover:text-accent-foreground`;
const BTN_PRIMARY = `${BTN} h-10 bg-primary px-4 text-primary-foreground hover:bg-primary/90`;
const BTN_GHOST = `${BTN} h-10 px-3 hover:bg-accent hover:text-accent-foreground`;
const BTN_ICON = `${BTN} h-10 w-10 hover:bg-accent hover:text-accent-foreground`;
const BTN_DANGER = `${BTN} h-10 border border-destructive/40 bg-background px-4 text-destructive hover:bg-destructive/10`;
const INPUT = 'w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring';

const svg = (d, s = 16) => `<svg width="${s}" height="${s}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">${d}</svg>`;
const I = {
  dl: svg('<path d="M12 3v12"/><path d="m7 10 5 5 5-5"/><path d="M5 21h14"/>'),
  up: svg('<path d="M12 15V3"/><path d="m7 8 5-5 5 5"/><path d="M5 21h14"/>'),
  copy: svg('<rect x="9" y="9" width="12" height="12" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/>'),
  x: svg('<path d="M18 6 6 18"/><path d="m6 6 12 12"/>'),
  ok: svg('<path d="M20 6 9 17l-5-5"/>'),
  trash: svg('<path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M19 6l-1 14H6L5 6"/>'),
  swap: svg('<path d="M17 3l4 4-4 4"/><path d="M3 7h18"/><path d="M7 21l-4-4 4-4"/><path d="M21 17H3"/>'),
  left: svg('<path d="m15 18-6-6 6-6"/>'),
  right: svg('<path d="m9 18 6-6-6-6"/>'),
  link: svg('<path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.5 1.5"/><path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.5-1.5"/>'),
  clock: svg('<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>'),
  play: svg('<path d="M7 4v16l13-8Z" fill="currentColor"/>', 22),
  search: svg('<circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/>'),
  plus: svg('<path d="M12 5v14"/><path d="M5 12h14"/>'),
};

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const dayStart = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const offOf = (d) => Math.round((dayStart(d) - dayStart(new Date())) / DAY);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
function fmtT(d) { let h = d.getHours(); const m = d.getMinutes(); const ap = h >= 12 ? 'PM' : 'AM'; h = h % 12 || 12; return `${h}${m ? `:${String(m).padStart(2, '0')}` : ''} ${ap}`; }
function fmtDay(d) { const n = offOf(d); if (n === 0) return 'Today'; if (n === 1) return 'Tomorrow'; if (n === -1) return 'Yesterday'; return `${DAYS[d.getDay()]} ${d.getDate()}`; }
function dur(ms) { const m = Math.max(1, Math.round(ms / 6e4)); if (m < 60) return `${m} min`; const h = Math.floor(m / 60); const r = m % 60; return `${h}h${r ? ` ${r}m` : ''}`; }
const size = (b) => (!b ? '' : b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const hue = (s) => { let h = 0; for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) % 360; return h; };
const isPhone = () => window.matchMedia('(max-width: 720px)').matches;
const dot = (k) => `<span class="inline-block h-2 w-2 shrink-0 rounded-full" style="background:${COLOR[k]}"></span>`;
const studioLink = (href) => (/^https:\/\/studio\.youtube\.com\//.test(href || '') ? `<a class="text-xs font-semibold underline hover:text-foreground" href="${esc(href)}" target="_blank" rel="noopener">Studio</a>` : '');
// Results tab
const RES_DAYS = [7, 30, 90];
const RES_COLS = [['title', 'Video'], ['platform', 'Platform'], ['posted_at', 'Posted'], ['views', 'Views'], ['likes', 'Likes'], ['comments', 'Comments'], ['score', 'Score']];
const RES_ICON = {
  refresh: svg('<path d="M21 12a9 9 0 1 1-2.6-6.4"/><path d="M21 4v5h-5"/>'),
  ext: svg('<path d="M14 4h6v6"/><path d="M20 4 10 14"/><path d="M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5"/>'),
};
const fmtN = (n) => (n == null ? '-' : Number(n).toLocaleString());
const nWord = (n, word) => `${fmtN(n)} ${word}${n === 1 ? '' : 's'}`;
const compactN = (n) => new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(n);
const dayWord = (d) => { const s = fmtDay(d); return ['Today', 'Tomorrow', 'Yesterday'].includes(s) ? s.toLowerCase() : s; };

// Resumable uploads (sendFile): files over 15 MB go to R2 in parts.
const UP_MIN = 15 * 1048576;
const UP_PARALLEL = 3;
const UP_RETRIES = 6;
const UP_STORE = 'dam-uploads';
// How long a half-done upload can be resumed: R2 drops it after 7 days and
// the daily cleanup keeps its file that long (Dam::Asset::RESUME_WINDOW; keep
// the two the same).
const UP_RESUME = 7 * DAY;
const upKey = (f, ctx) => `${f.name}|${f.size}|${f.lastModified}|${ctx}`;
const sleep = (ms) => new Promise((r) => { setTimeout(r, ms); });
const backoff = (n) => Math.min(30000, 2000 * 2 ** n) * (0.8 + Math.random() * 0.4);

// Series sheet (Library select, item sheet). Weekday keys are what the API takes.
const SERIES_PL = ['tiktok', 'youtube', 'facebook'];
// Why a post was left out of a series (Dam::Series left_over reason).
const SERIES_WHY = { 'past end date': 'past the end date', 'no free slot': 'no free slot by the end date', 'over the count': 'over the count' };
const SERIES_PATTERNS = [['d1', 'Every day'], ['d2', 'Every 2 days'], ['d3', 'Every 3 days'], ['weekly', 'Weekly'], ['days', 'Days of week'], ['twice', '2 a day']];
const WDAY = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const I_SPARK = svg('<path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M6 18l2.5-2.5M15.5 8.5 18 6"/>', 14);
const I_SERIES = svg('<path d="m17 2 4 4-4 4"/><path d="M3 11v-1a4 4 0 0 1 4-4h14"/><path d="m7 22-4-4 4-4"/><path d="M21 13v1a4 4 0 0 1-4 4H3"/>');

export default class extends Controller {
  static targets = ['view', 'back', 'where', 'sendBar', 'layer', 'drop', 'toast', 'uploadInput', 'sendInput', 'fileInput'];

  static values = { api: String, configured: Boolean };

  connect() {
    this.data_ = { items: [], posts: [], rule: { cadence: '1d', platforms: ['tiktok', 'youtube', 'facebook'] } };
    this.ui = { kind: '', q: '', capTab: 'default', phonePlat: 'tiktok', early: false, got: new Set(), report: null, homeType: null, sel: null, slot: null, layer: null, send: null, busy: 0 };
    this.csrf = document.querySelector('meta[name="csrf-token"]')?.content;
    this.onHash = () => { this.closeLayer(); this.render(); window.scrollTo(0, 0); };
    window.addEventListener('hashchange', this.onHash);
    this.element.addEventListener('click', (e) => this.click(e));
    this.element.addEventListener('input', (e) => this.input(e));
    this.element.addEventListener('change', (e) => this.change(e));
    this.bindDrop();
    this.bindDrag();
    this.seriesInit();
    this.onKey = (e) => { if (e.key === 'Escape') this.closeLayer(); };
    document.addEventListener('keydown', this.onKey);
    this.load();
    this.upHint();
    this.timer = setInterval(() => { if (!this.ui.layer && !this.ui.busy) this.load(); else this.renderView(); }, 60000);
    // The cooldown ring counts down on its own; nothing else on Home is re-drawn.
    this.cdTimer = setInterval(() => { if (this.route() === 'home' && this.ui.homeType === 'cool' && !this.ui.layer) this.renderView(); }, 30000);
    this.onResize = () => { if (this.route() === 'home') this.renderDock(); };
    window.addEventListener('resize', this.onResize);
  }

  disconnect() {
    window.removeEventListener('hashchange', this.onHash);
    document.removeEventListener('keydown', this.onKey);
    clearInterval(this.timer);
    clearInterval(this.cdTimer);
    window.removeEventListener('resize', this.onResize);
  }

  // ---------- data ----------
  async api(method, path, body) {
    const res = await fetch(this.apiValue + path, {
      method,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': this.csrf },
      credentials: 'same-origin',
      body: body ? JSON.stringify(body) : undefined,
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw Object.assign(new Error(json.message || json.error || `HTTP ${res.status}`), { status: res.status });
    return json;
  }

  async load() {
    try {
      const d = await this.api('GET', '/state');
      this.data_ = d;
      this.items = new Map(d.items.map((i) => [i.id, i]));
      d.posts.forEach((p) => p.platforms.forEach((s) => {
        s.when = s.scheduled_for ? new Date(s.scheduled_for) : null;
        s.postedAt = s.posted_at ? new Date(s.posted_at) : null;
      }));
      this.render();
    } catch (e) {
      this.toast(`Couldn't load: ${e.message}`);
    }
  }

  item(id) { return this.items?.get(id); }

  post(id) { return this.data_.posts.find((p) => p.id === id); }

  slot(p, k) { return p.platforms.find((s) => s.platform === k); }

  ttQueue() {
    const out = [];
    this.data_.posts.forEach((p) => { const s = this.slot(p, 'tiktok'); if (s && s.state !== 'posted') out.push({ p, s }); });
    return out.sort((a, b) => (a.s.queue_position ?? 1e9) - (b.s.queue_position ?? 1e9) || a.p.id - b.p.id);
  }

  lastTikTok() {
    let t = null;
    this.data_.posts.forEach((p) => { const s = this.slot(p, 'tiktok'); if (s?.postedAt && (!t || s.postedAt > t)) t = s.postedAt; });
    return t;
  }

  route() { const h = window.location.hash.replace('#', ''); return ['plan', 'library', 'results', 'stats'].includes(h) ? h : 'home'; }

  // ---------- render ----------
  render() {
    const r = this.route();
    this.backTarget.classList.toggle('hidden', r === 'home');
    this.backTarget.classList.toggle('inline-flex', r !== 'home');
    this.whereTarget.textContent = { home: 'DAM', plan: 'Plan', library: 'Library', results: 'Results', stats: 'Stats' }[r];
    this.renderView();
    this.renderLayer();
  }

  renderView() {
    const r = this.route();
    if (r === 'plan') {
      const old = this.viewTarget.querySelector('[data-dam-plan]');
      const left = old ? old.scrollLeft : null;
      this.viewTarget.innerHTML = this.planHtml();
      const box = this.viewTarget.querySelector('[data-dam-plan]');
      const today = box?.querySelector('[data-today]');
      if (box && today) box.scrollLeft = left ?? Math.max(0, today.offsetLeft - 150);
    }
    else if (r === 'library') this.renderLibrary();
    else if (r === 'results') this.renderResults();
    else if (r === 'stats') this.renderStats();
    else this.viewTarget.innerHTML = this.homeHtml();
    this.renderDock();
  }

  // Home's one big action: Send out on a phone, Drop videos on a laptop.
  // A receipt card already has its own action (Done), so the dock steps aside.
  renderDock() {
    const show = this.route() === 'home' && !this.ui.report;
    this.sendBarTarget.classList.toggle('hidden', !show);
    if (!show) return;
    const phone = isPhone();
    // While a TikTok is due, "Posted on TikTok" is the main action; Send out steps back.
    const quiet = this.ui.homeType === 'post';
    const html = phone
      ? quiet
        ? `<button class="${BTN_OUTLINE} h-11 w-full rounded-xl bg-background/95 shadow" data-act="pickSendOut">${svg('<path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/>')}Send out to YouTube</button>`
        : `<button class="${BTN_PRIMARY} h-14 w-full rounded-xl text-lg shadow-lg" data-act="pickSendOut">${svg('<path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/>', 20)}Send out</button>`
      : `<button class="${BTN} h-16 w-full rounded-xl border-2 border-dashed border-primary/50 bg-background text-lg font-semibold text-primary shadow-lg hover:bg-accent" data-act="pickUpload">${svg('<path d="M12 15V3"/><path d="m7 8 5-5 5 5"/><path d="M5 21h14"/>', 20)}Drop videos</button>`;
    const mode = `${phone ? 'phone' : 'laptop'}${quiet ? '-quiet' : ''}`;
    if (this.sendBarTarget.dataset.mode !== mode) { this.sendBarTarget.innerHTML = `<div class="mx-auto max-w-[520px]">${html}</div>`; this.sendBarTarget.dataset.mode = mode; }
  }

  thumb(it, cls = 'w-full', ratio = '9/16', playable = false) {
    const bg = `aspect-ratio:${ratio};background:linear-gradient(165deg,hsl(${hue(it.title)} 55% 30%),hsl(${(hue(it.title) + 50) % 360} 65% 12%))`;
    let media = '';
    if (it.preview_url && it.preview_type?.startsWith('image/')) media = `<img src="${esc(it.preview_url)}" alt="" class="absolute inset-0 h-full w-full object-cover" loading="lazy">`;
    else if (it.preview_url && it.preview_type?.startsWith('video/')) media = `<video src="${esc(it.preview_url)}#t=0.5" muted playsinline preload="metadata" class="absolute inset-0 h-full w-full object-cover"></video>`;
    const isVideo = it.preview_url && it.preview_type?.startsWith('video/');
    const tap = playable && isVideo;
    const play = (tap || (!media && (it.kind === 'video' || it.kind === 'hook'))) ? `<span class="pointer-events-none absolute inset-0 m-auto flex h-12 w-12 items-center justify-center rounded-full bg-black/40 text-white" data-play-icon>${I.play}</span>` : '';
    return `<div class="relative overflow-hidden rounded-lg ${cls} ${tap ? 'cursor-pointer' : ''}" style="${bg}" ${tap ? 'data-act="play" role="button" aria-label="Play"' : ''}>${media}${play}</div>`;
  }

  // Home shows ONE thing at a time: a receipt after a send or a batch, the
  // 3h TikTok cooldown, an empty queue, or the next TikTok to post.
  thing() {
    if (this.ui.report) return { type: this.ui.report.type };
    const q = this.ttQueue();
    const last = this.lastTikTok();
    const left = last ? +last + COOLDOWN - Date.now() : 0;
    if (left <= 0) this.ui.early = false;
    if (left > 0 && !this.ui.early) return { type: 'cool', left, last, next: q[0] || null };
    if (!q.length) return { type: 'empty' };
    return { type: 'post', x: q[0], n: q.length, early: left > 0 };
  }

  homeHtml() {
    const t = this.thing();
    this.ui.homeType = t.type;
    let card = '';
    if (t.type === 'sent') card = this.sentCard();
    else if (t.type === 'batch') card = this.batchCard();
    if (!card && (t.type === 'sent' || t.type === 'batch')) { this.ui.report = null; return this.homeHtml(); }
    if (t.type === 'cool') card = this.coolCard(t);
    else if (t.type === 'empty') card = `<article class="flex flex-col items-center gap-3 rounded-2xl border border-border bg-background p-8 text-center shadow-sm"><span class="flex h-14 w-14 items-center justify-center rounded-full bg-accent text-accent-foreground">${svg('<path d="M20 6 9 17l-5-5"/>', 28)}</span><h1 class="text-xl font-bold">TikTok queue is empty</h1>${this.handledAll()}</article>`;
    else if (t.type === 'post') card = this.postCard(t);
    return `<div class="mx-auto flex max-w-[520px] flex-col gap-3 px-3 pt-4">${card}</div>`;
  }

  postCard(t) {
    const { p } = t.x;
    const it = this.item(p.item_id);
    const got = this.ui.got.has(p.id);
    const cap = it.captions?.tiktok || it.captions?.default;
    const file = p.file_id || it.post_file_id;
    return `<article class="flex flex-col gap-4 rounded-2xl border border-border bg-background p-4 shadow-sm">
      <div class="flex items-center gap-2"><h1 class="flex-1 text-xl font-bold">Post this on TikTok</h1>${t.early ? '<span class="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">Early</span>' : t.n > 1 ? `<span class="text-sm text-muted-foreground">1 of ${t.n}</span>` : ''}</div>
      <div class="flex flex-col gap-4 sm:flex-row">
        <div class="mx-auto w-[46%] max-w-[180px] shrink-0 sm:mx-0 sm:w-[38%]">${this.thumb(it, 'w-full', '9/16', true)}</div>
        <div class="flex min-w-0 flex-1 flex-col gap-3">
          <h2 class="font-semibold leading-snug">${esc(it.title)}</h2>
          <button class="${got ? BTN_OUTLINE : BTN_PRIMARY} h-14 w-full rounded-xl text-lg" data-act="dl" data-p="${p.id}" data-file="${file}">${got ? `${I.ok}Downloaded` : `${I.dl}Download`}</button>
          ${cap ? `<button class="${BTN_OUTLINE} h-11 w-full" data-act="copyCap" data-item="${it.id}" data-k="tiktok">${I.copy}Copy caption</button>` : `<button class="${BTN_GHOST} h-11 w-full text-muted-foreground" data-act="openItem" data-item="${it.id}">No caption · Add</button>`}
          <button class="flex h-[72px] w-full items-center gap-3 rounded-xl border-2 px-4 text-left text-lg font-bold transition-colors ${got ? 'border-primary bg-primary text-primary-foreground' : 'border-primary/60 text-foreground hover:border-primary'}" data-act="postedTT" data-p="${p.id}" role="checkbox" aria-checked="false">
            <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border-2 border-current"></span>Posted on TikTok</button>
          <div class="flex gap-2"><button class="${BTN_OUTLINE} h-11 flex-1" data-act="nextTT" data-p="${p.id}" title="Post the next one first; this one comes right after">Next</button><button class="${BTN_OUTLINE} h-11 flex-1" data-act="skipTT" data-p="${p.id}" title="Take it out of the queue (stays in the Library)">Skip</button><button class="${BTN_OUTLINE} h-11 flex-1 text-destructive" data-act="delCard" data-item="${it.id}">${I.trash}Delete</button></div>
        </div>
      </div>
      ${it.lane === 'tiktok_first' && !p.platforms.some((s) => s.platform === 'facebook') ? `<p class="flex items-center gap-1 text-xs text-muted-foreground">${dot('facebook')}Facebook gets it after you post · never YouTube</p>` : this.handledFor(p)}
    </article>`;
  }

  // After posting: "Posted ✓", then the wait. The next video stays hidden
  // until the cooldown ends (Andrew goes back to scrolling, not downloading).
  coolCard(t) {
    const until = new Date(+t.last + COOLDOWN);
    const f = Math.min(1, Math.max(0, (Date.now() - t.last) / COOLDOWN));
    const fresh = Date.now() - t.last < 10 * 6e4;
    return `<article class="flex flex-col items-center gap-4 rounded-2xl border border-border bg-background p-5 shadow-sm">
      ${fresh ? `<div class="flex items-center gap-2 text-lg font-bold text-primary"><span class="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground">${svg('<path d="M20 6 9 17l-5-5"/>', 18)}</span>Posted on TikTok</div>` : ''}
      <div class="relative h-[200px] w-[200px]">
        <svg viewBox="0 0 200 200" class="h-full w-full -rotate-90" aria-hidden="true"><circle cx="100" cy="100" r="86" fill="none" stroke="hsl(var(--secondary))" stroke-width="12"/><circle cx="100" cy="100" r="86" fill="none" stroke="hsl(var(--primary))" stroke-width="12" stroke-linecap="round" stroke-dasharray="${RING.toFixed(1)}" stroke-dashoffset="${(RING * (1 - f)).toFixed(1)}"/></svg>
        <div class="absolute inset-0 flex flex-col items-center justify-center text-center"><b class="text-3xl font-bold">${fmtT(until)}</b><span class="text-sm text-muted-foreground">Next TikTok · in ${dur(+until - Date.now())}</span></div>
      </div>
      ${t.next ? '<button class="text-xs font-semibold text-muted-foreground underline hover:text-foreground" data-act="early">Post now anyway</button>' : ''}
      ${this.handledAll()}
    </article>`;
  }

  // Claude's platforms, shown as handled, not as chores. Facebook is paused.
  handledFor(p) {
    const ss = p.platforms.filter((s) => s.platform !== 'tiktok');
    if (!ss.length) return '';
    const bit = (s) => {
      if (PAUSED.includes(s.platform) && s.state !== 'posted') return `<span class="inline-flex items-center gap-1 opacity-60">${dot(s.platform)}${LABEL[s.platform]} paused</span>`;
      const when = s.state === 'posted' ? 'posted' : s.when ? fmtDay(s.when) : 'next run';
      return `<span class="inline-flex items-center gap-1">${dot(s.platform)}${LABEL[s.platform]} ${when}</span>`;
    };
    return `<a href="#plan" class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground hover:text-foreground" title="Claude posts these">${I_SPARK}${ss.map(bit).join('')}</a>`;
  }

  handledAll() {
    let day = null;
    const ks = new Set();
    this.data_.posts.forEach((p) => p.platforms.forEach((s) => {
      if (s.platform === 'tiktok' || PAUSED.includes(s.platform) || s.state === 'posted' || !s.when) return;
      ks.add(s.platform); if (!day || s.when > day) day = s.when;
    }));
    if (!day) return '';
    return `<a href="#plan" class="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground" title="Claude posts these">${I_SPARK}${PL.filter((k) => ks.has(k)).map(dot).join('')}Handled through ${fmtDay(day)}</a>`;
  }

  // After Send out: where it's going, filled in as Claude's run books it.
  sentCard() {
    const it = this.item(this.ui.report.itemId);
    if (!it) return '';
    const ss = this.data_.posts.filter((p) => p.item_id === it.id).flatMap((p) => p.platforms.filter((s) => s.platform !== 'tiktok').map((s) => ({ p, s })));
    const ran = ss.length && ss.every(({ s }) => s.state === 'posted' || PAUSED.includes(s.platform));
    const row = ({ s }) => {
      let st;
      if (s.state === 'posted') st = `${I.ok}Posted${s.url ? `<button class="${BTN_ICON} h-8 w-8" data-act="copyText" data-text="${esc(s.url)}" aria-label="Copy ${LABEL[s.platform]} link">${I.copy}</button>` : ''}`;
      else if (PAUSED.includes(s.platform)) st = 'Paused';
      else if (s.state === 'scheduled') st = `${I.clock}${s.when ? fmtET(s.when) : 'Booked'}`;
      else st = 'Next run';
      return `<div class="flex h-11 items-center gap-2 border-b border-border last:border-0"><b class="flex flex-1 items-center gap-2 text-sm">${dot(s.platform)}${LABEL[s.platform]}</b><span class="flex items-center gap-1 text-sm text-muted-foreground">${st}</span></div>`;
    };
    return `<article class="flex flex-col gap-4 rounded-2xl border border-border bg-background p-4 shadow-sm">
      <div class="flex items-center gap-3"><div class="w-14 shrink-0 [&_[data-play-icon]]:hidden">${this.thumb(it)}</div><div class="min-w-0 flex-1"><h1 class="text-xl font-bold">${ran ? 'Out' : 'Going out'}</h1><p class="truncate text-sm text-muted-foreground">${esc(it.title)}</p></div></div>
      <div class="flex flex-col">${ss.map(row).join('')}</div>
      <div class="grid grid-cols-2 gap-2"><button class="${BTN_OUTLINE} h-12" data-act="pickSendOut">Send another</button><button class="${BTN_PRIMARY} h-12" data-act="reportDone">Done</button></div>
    </article>`;
  }

  // After a batch: where it all landed, without opening Plan.
  batchCard() {
    const ids = new Set(this.ui.report.itemIds);
    const made = this.data_.posts.filter((p) => ids.has(p.item_id));
    if (!made.length) return '';
    const q = this.ttQueue();
    const qs = made.map((p) => q.findIndex((x) => x.p.id === p.id) + 1).filter((n) => n > 0).sort((a, b) => a - b);
    const rows = [];
    if (qs.length) rows.push([dot('tiktok') + 'TikTok', `#${qs[0]}${qs.length > 1 ? `–#${qs[qs.length - 1]}` : ''} in the queue`]);
    CLAUDE.forEach((k) => {
      const ts = made.map((p) => this.slot(p, k)?.when).filter(Boolean).sort((a, b) => a - b);
      if (!ts.length) return;
      if (PAUSED.includes(k)) { rows.push([dot(k) + LABEL[k], 'Paused']); return; }
      const hrs = [...new Set(ts.map(etHour))].sort((a, b) => a - b).map(etLabel);
      rows.push([dot(k) + LABEL[k], `${fmtDay(ts[0])}${ts.length > 1 ? ` – ${fmtDay(ts[ts.length - 1])}` : ''} · ${hrs.join(' + ')} ET`]);
    });
    const firstWhen = (p) => p.platforms.filter((s) => s.platform !== 'tiktok' && s.when).map((s) => +s.when).sort((a, b) => a - b)[0];
    const film = made.slice().sort((a, b) => (firstWhen(a) || 0) - (firstWhen(b) || 0)).map((p) => {
      const it = this.item(p.item_id); const w = firstWhen(p); const n = q.findIndex((x) => x.p.id === p.id) + 1;
      return `<div class="flex w-16 shrink-0 flex-col gap-1 [&_[data-play-icon]]:hidden">${this.thumb(it)}<span class="text-center text-[11px] font-semibold text-muted-foreground">${w ? fmtDay(new Date(w)) : n ? `#${n}` : ''}</span></div>`;
    }).join('');
    return `<article class="flex flex-col gap-4 rounded-2xl border border-border bg-background p-4 shadow-sm">
      <h1 class="text-xl font-bold">${made.length} scheduled</h1>
      <div class="flex flex-col">${rows.map(([l, r]) => `<div class="flex h-11 items-center gap-2 border-b border-border last:border-0"><b class="flex flex-1 items-center gap-2 text-sm">${l}</b><span class="text-sm text-muted-foreground">${r}</span></div>`).join('')}</div>
      <div class="flex gap-2 overflow-x-auto pb-1">${film}</div>
      <div class="grid grid-cols-2 gap-2"><a href="#plan" class="${BTN_OUTLINE} h-12" data-act="go" data-to="plan">See in Plan</a><button class="${BTN_PRIMARY} h-12" data-act="reportDone">Done</button></div>
    </article>`;
  }

  everythingHtml(top) {
    const link = (href, label, icon) => `<a href="${href}" class="flex h-12 items-center gap-3 rounded-md px-3 font-semibold hover:bg-accent" data-act="go" data-to="${href.slice(1)}">${icon}${label}</a>`;
    const btn = (act, label, icon) => `<button class="flex h-12 items-center gap-3 rounded-md px-3 text-left font-semibold hover:bg-accent" data-act="${act}">${icon}${label}</button>`;
    return `${top('Everything')}<nav class="flex flex-col gap-1 overflow-y-auto p-3">
      ${link('#plan', 'Plan', I.clock)}
      ${link('#library', 'Library', I.search)}
      ${link('#results', 'Results', svg('<path d="M3 3v18h18"/><path d="m7 15 4-4 3 3 5-6"/>'))}
      ${link('#stats', 'Stats', svg('<path d="M4 20V10"/><path d="M10 20V4"/><path d="M16 20v-7"/><path d="M22 20H2"/>'))}
      ${btn('openLinks', 'Shipped links', I.link)}
      ${btn('openRule', 'Rule', I_SERIES)}
      ${btn('pickUpload', 'Upload videos', I.up)}
    </nav>`;
  }

  stateMark(s) {
    const e = s.effective_state;
    const title = `${LABEL[s.platform]}: ${STATE_LABEL[e]}${s.live_status === 'live' ? ', checked live' : s.live_status === 'unchecked' ? ', not checked' : s.live_status === 'not_live' && e !== 'not_live' ? ', not live' : ''}`;
    const base = 'inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] text-white';
    if (e === 'posted') return `<span class="${base}" style="background:${COLOR[s.platform]}" title="${esc(title)}">${svg('<path d="M20 6 9 17l-5-5"/>', 11)}</span>`;
    if (e === 'scheduled') return `<span class="${base}" style="background:${COLOR[s.platform]};opacity:.55" title="${esc(title)}">${svg('<circle cx="12" cy="12" r="8"/><path d="M12 8v4l2 2"/>', 11)}</span>`;
    if (e === 'missed' || e === 'not_live') return `<span class="${base} ring-2 ring-destructive" style="background:${COLOR[s.platform]};opacity:.4" title="${esc(title)}">!</span>`;
    return `<span class="${base} border-2" style="border-color:${COLOR[s.platform]};background:transparent" title="${esc(title)}"></span>`;
  }

  // A time set exactly by hand: kept as is, and "add to the end" ignores it.
  pinMark(s, withLabel = false) {
    if (!s.pinned || s.state === 'posted') return '';
    const pin = svg('<path d="M12 17v5"/><path d="M9 10.8a2 2 0 0 1-1.1 1.8l-1.8.9A2 2 0 0 0 5 15.2V17h14v-1.8a2 2 0 0 0-1.1-1.8l-1.8-.9a2 2 0 0 1-1.1-1.8V6a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4z"/>', 11);
    if (withLabel) return `<span class="inline-flex items-center gap-1">${pin}Exact time</span>`;
    return `<span class="ml-auto shrink-0 text-muted-foreground" title="Exact time">${pin}</span>`;
  }

  // Library: an item with no uploaded file still shows (its captions and link
  // are kept), marked so; the item sheet's Add file fills it.
  noFile(it, cls = '') { return it.files?.length ? '' : `<span class="text-xs font-normal text-muted-foreground ${cls}">No file</span>`; }

  // Plan: TikTok as an ordered queue, every other platform as dated slots.
  planHtml() {
    const chip = (p, s, extra = '') => {
      const it = this.item(p.item_id);
      const e = s.effective_state;
      const tone = { posted: 'opacity-60', scheduled: '', planned: 'border-dashed', missed: 'border-destructive bg-destructive/10', not_live: 'border-destructive bg-destructive/10', queued: '' }[e] || '';
      return `<button class="flex w-full min-w-0 items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1 text-left text-xs font-semibold shadow-sm hover:border-primary select-none ${tone}" data-act="slot" data-p="${p.id}" data-k="${s.platform}" ${e === 'posted' ? '' : 'data-drag'} title="${esc(it.title)} · ${STATE_LABEL[e]}">
        <i class="h-2 w-2 shrink-0 rounded-full" style="background:${COLOR[s.platform]}"></i>${extra}<span class="truncate">${esc(it.title)}</span>${this.pinMark(s)}${e === 'posted' ? `<span class="ml-auto text-primary">${svg('<path d="M20 6 9 17l-5-5"/>', 12)}</span>` : ''}</button>`;
    };
    const today = dayStart(new Date());
    const slotsOn = (k, o) => {
      const out = [];
      this.data_.posts.forEach((p) => {
        const s = this.slot(p, k);
        const t = k === 'tiktok' ? s?.postedAt : (s?.when || s?.postedAt);
        if (s && t && offOf(t) === o && (k !== 'tiktok' || s.state === 'posted')) out.push({ p, s, t });
      });
      return out.sort((a, b) => a.t - b.t);
    };
    const q = this.ttQueue();

    if (isPhone()) {
      const k = this.ui.phonePlat;
      let h = `<div class="sticky top-14 z-10 flex gap-1 overflow-x-auto border-b border-border bg-background px-3 py-2">${PL.map((p) => `<button class="${BTN} h-9 shrink-0 rounded-full border px-3 ${p === k ? 'border-primary bg-accent text-accent-foreground' : 'border-border'}" data-act="phonePlat" data-k="${p}">${dot(p)}${LABEL[p]}${p === 'tiktok' && q.length ? ` <em class="not-italic text-muted-foreground">${q.length}</em>` : ''}</button>`).join('')}</div><div class="flex flex-col px-3 py-2">`;
      const row = (label, inner, cls = '', cell = '') => `<div class="flex min-h-[48px] items-center gap-3 border-b border-border py-1.5 ${cls}" ${cell ? `data-cell="${cell}"` : ''}><div class="w-14 shrink-0 text-xs font-semibold text-muted-foreground">${label}</div><div class="flex min-w-0 flex-1 flex-col gap-1">${inner}</div></div>`;
      if (k === 'tiktok') {
        for (let o = PLAN_START; o <= 0; o += 1) {
          const d = addDays(today, o);
          const xs = slotsOn('tiktok', o);
          if (xs.length) h += row(o === 0 ? 'Today' : `${DAYS[d.getDay()]} ${d.getDate()}`, xs.map((x) => chip(x.p, x.s)).join(''), 'opacity-70');
        }
        q.forEach((x, i) => { h += row(`#${i + 1}`, chip(x.p, x.s), '', 'tiktok|Q'); });
        if (q.length) h += row(`#${q.length + 1}`, '', '', 'tiktok|Q');
        if (!q.length) h += '<p class="py-6 text-center text-sm text-muted-foreground">TikTok queue is empty</p>';
      } else {
        for (let o = PLAN_START; o <= PLAN_END; o += 1) {
          const d = addDays(today, o);
          const xs = slotsOn(k, o);
          h += row(o === 0 ? 'Today' : `${DAYS[d.getDay()]} ${d.getDate()}`, xs.map((x) => chip(x.p, x.s, `<span class="shrink-0 font-normal text-muted-foreground">${fmtT(x.t)}</span>`)).join(''), `${o < 0 ? 'opacity-70' : ''} ${o === 0 ? 'bg-accent/40' : ''}`, `${k}|${o}`);
        }
      }
      return `${h}</div>`;
    }

    const cols = PLAN_END - PLAN_START + 1;
    let h = `<div class="overflow-x-auto px-3 py-3" data-dam-plan><div class="grid w-max gap-px rounded-lg border border-border bg-border" style="grid-template-columns:140px repeat(${cols},110px)">`;
    h += '<div class="sticky left-0 z-10 bg-background"></div>';
    for (let o = PLAN_START; o <= PLAN_END; o += 1) {
      const d = addDays(today, o);
      h += `<div class="bg-background px-2 py-1.5 text-center text-xs ${o === 0 ? 'font-bold text-primary' : 'text-muted-foreground'}" ${o === 0 ? 'data-today' : ''}>${o === 0 ? 'Today' : DAYS[d.getDay()]} <b class="text-foreground">${d.getDate()}</b></div>`;
    }
    PL.forEach((k) => {
      h += `<div class="sticky left-0 z-10 flex items-center gap-2 bg-background px-3 text-sm font-semibold">${dot(k)}${LABEL[k]}${k === 'tiktok' ? `<span class="ml-auto whitespace-nowrap text-xs font-normal text-muted-foreground">${q.length}</span>` : ''}</div>`;
      if (k === 'tiktok') {
        for (let o = PLAN_START; o < 0; o += 1) h += `<div class="flex min-h-[64px] flex-col gap-1 bg-background/70 p-1">${slotsOn('tiktok', o).map((x) => chip(x.p, x.s)).join('')}</div>`;
        h += `<div class="flex min-h-[64px] items-start gap-1 bg-accent/30 p-1" style="grid-column:span ${PLAN_END + 1}" data-cell="tiktok|Q">${q.map((x, i) => `<div class="w-[104px] shrink-0">${chip(x.p, x.s, `<em class="shrink-0 not-italic text-muted-foreground">#${i + 1}</em>`)}</div>`).join('') || '<span class="p-2 text-xs text-muted-foreground">Queue empty</span>'}</div>`;
        return;
      }
      for (let o = PLAN_START; o <= PLAN_END; o += 1) {
        h += `<div class="flex min-h-[64px] flex-col gap-1 p-1 ${o < 0 ? 'bg-background/70' : o === 0 ? 'bg-accent/30' : 'bg-background'}" data-cell="${k}|${o}">${slotsOn(k, o).map((x) => chip(x.p, x.s)).join('')}</div>`;
      }
    });
    return `${h}</div></div>`;
  }

  // Library: every item, newest first.
  renderLibrary() {
    const search = this.viewTarget.querySelector('[data-lib-q]');
    if (!search) {
      this.viewTarget.innerHTML = `<div class="mx-auto flex max-w-6xl flex-col gap-3 px-3 pt-4">
        <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
          <label class="relative flex-1"><span class="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">${I.search}</span><input type="search" data-lib-q class="${INPUT} h-10 pl-9" placeholder="Search" value="${esc(this.ui.q)}" aria-label="Search"></label>
          <div class="flex gap-1 overflow-x-auto" data-lib-kinds></div>
        </div>
        <div data-lib-rows></div></div>`;
    }
    const kinds = this.viewTarget.querySelector('[data-lib-kinds]');
    kinds.innerHTML = [''].concat(KINDS).map((k) => `<button class="${BTN} h-9 shrink-0 rounded-full border px-3 ${this.ui.kind === k ? 'border-primary bg-accent text-accent-foreground' : 'border-border bg-background'}" data-act="kind" data-k="${k}">${k ? k[0].toUpperCase() + k.slice(1) : 'All'}</button>`).join('');
    const q = this.ui.q.trim().toLowerCase();
    const list = this.data_.items.filter((it) => !(it.pending_upload && !it.files?.length) && (!this.ui.kind || it.kind === this.ui.kind) && (!q || `${it.title} ${it.link || ''} ${it.files.map((f) => f.filename).join(' ')}`.toLowerCase().includes(q)));
    const rows = this.viewTarget.querySelector('[data-lib-rows]');
    if (!list.length) { rows.innerHTML = '<p class="py-10 text-center text-muted-foreground">Nothing here.</p>'; return; }
    const ttCell = (it) => {
      const ss = this.data_.posts.filter((p) => p.item_id === it.id).map((p) => ({ p, s: this.slot(p, 'tiktok') })).filter((x) => x.s);
      const queued = ss.find((x) => x.s.state !== 'posted');
      if (queued) return `#${this.ttQueue().findIndex((y) => y.p.id === queued.p.id) + 1}`;
      const posted = ss.filter((x) => x.s.postedAt).sort((a, b) => b.s.postedAt - a.s.postedAt)[0];
      return posted ? `${svg('<path d="M20 6 9 17l-5-5"/>', 12)} ${fmtDay(posted.s.postedAt)}` : '';
    };
    const marks = (it) => {
      const latest = {};
      this.data_.posts.filter((p) => p.item_id === it.id).forEach((p) => p.platforms.forEach((s) => { if (s.platform !== 'tiktok') latest[s.platform] = s; }));
      return CLAUDE.filter((k) => latest[k]).map((k) => this.stateMark(latest[k])).join('');
    };
    if (isPhone()) {
      rows.innerHTML = `<div class="flex flex-col divide-y divide-border rounded-lg border border-border bg-background">${list.map((it) => `
        <div class="flex items-center gap-3 p-2" data-act="openItem" data-item="${it.id}" role="button">
          <div class="w-12 shrink-0">${this.thumb(it, 'w-12', '1/1')}</div>
          <div class="min-w-0 flex-1"><div class="truncate text-sm font-semibold">${esc(it.title)}</div>
          <div class="flex items-center gap-2 text-xs text-muted-foreground"><span class="capitalize">${it.kind}</span>${this.noFile(it)}<span class="inline-flex items-center gap-1">${ttCell(it)}</span><span class="flex gap-1">${marks(it)}</span></div></div>
          ${it.post_file_id ? `<button class="${BTN_ICON}" data-act="dl" data-file="${it.post_file_id}" aria-label="Download">${I.dl}</button>` : ''}</div>`).join('')}</div>`;
      return;
    }
    rows.innerHTML = `<div class="overflow-hidden rounded-lg border border-border bg-background"><table class="w-full text-sm">
      <thead class="bg-secondary text-left text-xs text-muted-foreground"><tr><th class="w-16 p-2"></th><th class="p-2">Title</th><th class="p-2">Kind</th><th class="p-2">TikTok</th><th class="p-2">Others</th><th class="w-14 p-2"></th></tr></thead>
      <tbody class="divide-y divide-border">${list.map((it) => `
        <tr class="cursor-pointer hover:bg-accent/40" data-act="openItem" data-item="${it.id}">
          <td class="p-2"><div class="w-10">${this.thumb(it, 'w-10', '1/1')}</div></td>
          <td class="p-2 font-semibold">${esc(it.title)}${this.noFile(it, 'ml-2')}</td>
          <td class="p-2 capitalize text-muted-foreground">${it.kind}</td>
          <td class="p-2"><span class="inline-flex items-center gap-1">${ttCell(it)}</span></td>
          <td class="p-2"><span class="flex gap-1">${marks(it)}</span></td>
          <td class="p-2">${it.post_file_id ? `<button class="${BTN_ICON}" data-act="dl" data-file="${it.post_file_id}" aria-label="Download">${I.dl}</button>` : ''}</td>
        </tr>`).join('')}</tbody></table></div>`;
  }

  // ---------- layers: item sheet, slot popover, send out, menus ----------
  renderLayer() {
    const L = this.ui.layer;
    if (!L) { this.layerTarget.innerHTML = ''; document.body.style.overflow = ''; return; }
    document.body.style.overflow = 'hidden';
    const scrim = '<div class="fixed inset-0 z-40 bg-foreground/30" data-act="closeLayer"></div>';
    const panel = (inner, wide = false) => `${scrim}<div class="fixed inset-x-0 bottom-0 z-50 flex max-h-[92vh] flex-col overflow-hidden rounded-t-2xl bg-background shadow-xl sm:inset-y-0 sm:left-auto sm:right-0 sm:max-h-none ${wide ? 'sm:w-[460px]' : 'sm:w-[400px]'} sm:rounded-none" role="dialog">${inner}</div>`;
    const top = (title) => `<div class="flex items-center gap-2 border-b border-border px-4 py-3"><h2 class="min-w-0 flex-1 truncate text-lg font-bold">${title}</h2><button class="${BTN_ICON}" data-act="closeLayer" aria-label="Close">${I.x}</button></div>`;

    if (L === 'item') this.layerTarget.innerHTML = panel(this.itemHtml(top), true);
    else if (L === 'slot') this.layerTarget.innerHTML = panel(this.slotHtml(top));
    else if (L === 'send') this.layerTarget.innerHTML = panel(this.sendHtml(top));
    else if (L === 'menu') this.layerTarget.innerHTML = panel(this.menuHtml(top));
    else if (L === 'everything') this.layerTarget.innerHTML = panel(this.everythingHtml(top));
    else if (L === 'links') this.layerTarget.innerHTML = panel(this.linksHtml(top), true);
    else if (L === 'series') this.layerTarget.innerHTML = panel(this.seriesHtml(top), true);
    else if (L === 'booked') this.layerTarget.innerHTML = panel(this.bookedHtml());
    this.updateCount();
  }

  closeLayer() { if (!this.ui.layer) return; this.ui.layer = null; this.ui.sel = null; this.ui.ttFor = null; this.ui.slot = null; this.ui.send = null; this.renderLayer(); }

  openLayer(name) { this.ui.layer = name; this.renderLayer(); }

  itemHtml(top) {
    const it = this.item(this.ui.sel);
    if (!it) return top('Gone');
    const tab = this.ui.capTab;
    const cap = tab === 'default' ? (it.captions?.default || '') : (it.captions?.[tab] || '');
    const versions = it.files.filter((f) => f.version).sort((a, b) => a.version - b.version);
    const posts = this.data_.posts.filter((p) => p.item_id === it.id);
    const sec = (title, inner) => `<section class="flex flex-col gap-2"><h3 class="text-sm font-semibold text-muted-foreground">${title}</h3>${inner}</section>`;
    return `${top(`<input class="w-full rounded-md border border-transparent bg-transparent px-1 font-bold hover:border-input focus:border-primary focus:outline-none" data-in="title" value="${esc(it.title)}" aria-label="Title">`)}
      <div class="flex flex-col gap-5 overflow-y-auto p-4">
        <div class="flex gap-3"><div class="w-28 shrink-0">${this.thumb(it, 'w-full', '9/16', true)}</div>
          <div class="flex min-w-0 flex-1 flex-col gap-2">
            <button class="${BTN_PRIMARY} h-12 w-full text-base" data-act="dl" data-file="${it.post_file_id}" ${it.post_file_id ? '' : 'disabled'}>${I.dl}Download</button>
            <select class="${INPUT}" data-in="kind" aria-label="Kind">${KINDS.map((k) => `<option value="${k}" ${k === it.kind ? 'selected' : ''}>${k[0].toUpperCase() + k.slice(1)}</option>`).join('')}</select>
            <select class="${INPUT}" data-in="lane" aria-label="Where it goes">${LANES.map(([k, l]) => `<option value="${k}" ${k === (it.lane || 'all') ? 'selected' : ''}>${l}</option>`).join('')}</select>
            ${posts.length ? '' : `<button class="${BTN_OUTLINE} w-full" data-act="scheduleItem" data-item="${it.id}">${I.clock}Add to the plan</button>`}
            ${this.seriesItemBtn(it)}
            ${this.postedTTHtml(it, posts)}
          </div></div>
        ${sec('Versions', `<div class="flex flex-wrap items-center gap-2">${versions.map((f) => `<span class="rounded-md border border-border px-2 py-1 text-xs font-semibold ${f.id === it.post_file_id ? 'border-primary bg-accent text-accent-foreground' : ''}">v${f.version}</span>`).join('')}<button class="${BTN_OUTLINE} h-9" data-act="addFile" data-mode="version">${I.up}Upload new version</button></div>`)}
        ${sec('Files', `<div class="flex flex-col divide-y divide-border rounded-lg border border-border">${it.files.map((f) => `
          <div class="flex items-center gap-1 px-2 py-1"><span class="min-w-0 flex-1 truncate text-sm" title="${esc(f.filename)}">${esc(f.filename)}</span>${f.id === it.post_file_id ? '<span class="rounded bg-accent px-1.5 text-[11px] font-semibold text-accent-foreground">Post</span>' : ''}<span class="text-xs text-muted-foreground">${size(f.byte_size)}</span>
            <button class="${BTN_ICON} h-9 w-9" data-act="dl" data-file="${f.id}" aria-label="Download ${esc(f.filename)}">${I.dl}</button>
            <button class="${BTN_ICON} h-9 w-9" data-act="addFile" data-mode="replace" data-file="${f.id}" aria-label="Replace ${esc(f.filename)}">${I.swap}</button>
            <button class="${BTN_ICON} h-9 w-9 text-destructive" data-act="delFile" data-file="${f.id}" aria-label="Delete ${esc(f.filename)}">${I.trash}</button></div>`).join('') || '<p class="p-3 text-sm text-muted-foreground">No files</p>'}</div>
          <div class="flex gap-2">${it.files.length > 1 ? `<button class="${BTN_OUTLINE} h-9" data-act="dlAll">${I.dl}Download all</button>` : ''}<button class="${BTN_OUTLINE} h-9" data-act="addFile" data-mode="file">${I.plus}Add file</button></div>`)}
        ${sec('Caption', `<div class="flex gap-1 overflow-x-auto">${['default'].concat(PL).map((k) => `<button class="${BTN} h-8 shrink-0 rounded-full border px-2.5 text-xs ${tab === k ? 'border-primary bg-accent text-accent-foreground' : 'border-border'}" data-act="capTab" data-k="${k}">${k === 'default' ? 'All' : `${dot(k)}${LABEL[k]}`}</button>`).join('')}</div>
          ${tab === 'youtube' ? `<input class="${INPUT}" data-in="yt_title" placeholder="YouTube title" value="${esc(it.yt_title || '')}" aria-label="YouTube title">` : ''}
          <textarea rows="4" class="${INPUT}" data-in="caption" placeholder="${tab === 'default' ? 'Caption' : esc(it.captions?.default || 'Same as All')}" aria-label="Caption">${esc(cap)}</textarea>
          <div class="flex items-center justify-between"><span class="text-xs text-muted-foreground" data-count></span><button class="${BTN_GHOST} h-8" data-act="copyCap" data-item="${it.id}" data-k="${tab}">${I.copy}Copy</button></div>`)}
        ${sec('Link', `<div class="flex gap-2"><input class="${INPUT}" data-in="link" placeholder="bettersheets.co/…" value="${esc(it.link || '')}" aria-label="Link"><button class="${BTN_OUTLINE}" data-act="copyLink" data-item="${it.id}">${I.copy}</button></div>`)}
        ${posts.length ? sec('Posts', `<div class="flex flex-col gap-1">${posts.map((p) => this.postRowHtml(p)).join('')}</div>`) : ''}
        <button class="${BTN_DANGER} mt-2" data-act="delItem">${I.trash}Delete item</button>
      </div>`;
  }

  // One post on the item sheet; tapping it opens its first unposted slot.
  // All posted: when it went up, not the time it was once planned for.
  postRowHtml(p) {
    const open = p.platforms.find((s) => s.state !== 'posted') || p.platforms[0];
    const last = p.platforms.map((s) => s.postedAt).filter(Boolean).sort((a, b) => b - a)[0];
    const label = !p.platforms.some((s) => s.state !== 'posted') && last ? `Posted ${fmtDay(last)}, ${fmtT(last)}`
      : p.scheduled_at ? fmtDay(new Date(p.scheduled_at)) : 'TikTok queue';
    return `<button class="flex w-full items-center gap-2 rounded-md border border-border px-2 py-1.5 text-left text-sm hover:bg-accent" data-act="slot" data-p="${p.id}" data-k="${open?.platform}"><span class="flex-1">${label}${p.version > 1 ? ` · v${p.version}` : ''}</span><span class="flex gap-1">${p.platforms.map((s) => this.stateMark(s)).join('')}</span></button>`;
  }

  // Posted on TikTok by hand, out of queue order: its queued TikTok (or a
  // new post) is Posted at that time, the cooldown runs from it and the rest
  // of the queue keeps its order.
  postedTTHtml(it, posts) {
    if (posts.some((p) => this.slot(p, 'tiktok')?.state === 'posted')) return '';
    if (this.ui.ttFor !== it.id) return `<button class="${BTN_OUTLINE} w-full" data-act="ttOpen" data-item="${it.id}">${I.ok}Posted on TikTok</button>`;
    // No max: Android's picker jams when it's capped at this minute. The
    // server keeps it from being later than now.
    const local = (d) => new Date(+d - d.getTimezoneOffset() * 6e4).toISOString().slice(0, 16);
    const at = this.ui.ttAt || local(new Date());
    const ago = [[0, 'Now'], [15, '15m ago'], [30, '30m ago'], [60, '1h ago'], [120, '2h ago']];
    return `<div class="flex flex-col gap-2 rounded-lg border border-border p-2">
      <span class="text-sm">Posted on TikTok at</span>
      <div class="flex flex-wrap gap-1">${ago.map(([m, l]) => `<button class="${BTN} h-8 rounded-full border border-border px-2.5 text-xs" data-act="ttAgo" data-m="${m}">${l}</button>`).join('')}</div>
      <input type="datetime-local" class="${INPUT}" data-tt-at value="${at}" aria-label="Posted at">
      <div class="flex gap-2"><button class="${BTN_GHOST} flex-1" data-act="ttCancel">Cancel</button><button class="${BTN_PRIMARY} flex-1" data-act="ttSave" data-item="${it.id}">${I.ok}Mark posted</button></div></div>`;
  }

  async postedTikTokItem(itemId) {
    const v = this.layerTarget.querySelector('[data-tt-at]')?.value;
    const at = v ? new Date(v) : new Date();
    const r = await this.api('POST', `/items/${itemId}/posted_tiktok`, { at: at.toISOString() });
    const pid = r.post.id;
    this.ui.ttFor = null; this.ui.ttAt = null;
    this.ui.justPosted = Date.now();
    await this.load();
    this.renderLayer();
    // Undo: a post made just for this goes; a queued one goes back in the queue.
    const undo = async () => {
      this.ui.justPosted = null;
      if (r.created) await this.api('DELETE', `/posts/${pid}`);
      else await this.api('PATCH', `/posts/${pid}/platforms/tiktok`, { state: 'planned' });
      await this.load(); this.renderLayer();
    };
    this.toast(`Posted on TikTok · ${fmtT(at > new Date() ? new Date() : at)}`, { label: 'Undo', fn: undo });
  }

  slotHtml(top) {
    const [pid, k] = this.ui.slot;
    const p = this.post(pid);
    const s = p && this.slot(p, k);
    if (!s) return top('Gone');
    const it = this.item(p.item_id);
    const e = s.effective_state;
    const states = k === 'tiktok' ? [['planned', 'Queued'], ['posted', 'Posted']] : [['planned', e === 'missed' ? 'Missed' : 'Planned'], ['scheduled', e === 'not_live' ? 'Not live yet' : 'Scheduled'], ['posted', 'Posted']];
    const qn = k === 'tiktok' && s.state !== 'posted' ? this.ttQueue().findIndex((x) => x.p.id === p.id) + 1 : null;
    const when = qn ? `#${qn} in queue` : s.when ? `${fmtDay(s.when)}, ${fmtT(s.when)}` : s.postedAt ? `Posted ${fmtDay(s.postedAt)}` : '';
    return `${top(esc(it.title))}<div class="flex flex-col gap-3 overflow-y-auto p-4">
      <div class="flex items-center gap-2 text-sm text-muted-foreground">${dot(k)}<b class="text-foreground">${LABEL[k]}</b> · ${when}${this.pinMark(s, true)}</div>
      <div class="grid gap-1 rounded-lg bg-secondary p-1" style="grid-template-columns:repeat(${states.length},1fr)">${states.map(([v, l]) => `<button class="rounded-md px-2 py-2 text-sm font-semibold ${s.state === v ? 'bg-background shadow-sm' : 'text-muted-foreground'}" data-act="setState" data-st="${v}">${l}</button>`).join('')}</div>
      ${s.live_status ? `<p class="text-xs text-muted-foreground">${{ live: 'Checked live', unchecked: 'Not checked', not_live: 'Not live yet', not_live_final: 'Not live, stopped checking after 24h', awaiting_cancel: 'Books again once the old one is unscheduled' }[s.live_status] || ''}</p>` : ''}
      <button class="${BTN_PRIMARY} h-12 text-base" data-act="dl" data-file="${p.file_id || it.post_file_id}">${I.dl}Download</button>
      <div class="grid grid-cols-2 gap-2">
        <button class="${BTN_OUTLINE}" data-act="copyCap" data-item="${it.id}" data-k="${k}">${I.copy}Caption</button>
        <button class="${BTN_OUTLINE}" data-act="openItem" data-item="${it.id}">Details</button>
        ${s.state !== 'posted' ? `<button class="${BTN_OUTLINE}" data-act="move" data-d="-1">${I.left}${qn ? 'Earlier' : 'Day'}</button><button class="${BTN_OUTLINE}" data-act="move" data-d="1">${qn ? 'Later' : 'Day'}${I.right}</button>` : ''}
      </div>
      ${k !== 'tiktok' && s.state !== 'posted' ? `<label class="flex flex-col gap-1 text-sm">Time<input type="datetime-local" class="${INPUT}" data-in="slotTime" value="${s.when ? new Date(+s.when - s.when.getTimezoneOffset() * 6e4).toISOString().slice(0, 16) : ''}"></label>` : ''}
      <label class="flex flex-col gap-1 text-sm">Link<div class="flex gap-2"><input class="${INPUT}" data-in="slotUrl" placeholder="https://…" value="${esc(s.url || '')}">${s.url ? `<button class="${BTN_OUTLINE}" data-act="copyText" data-text="${esc(s.url)}">${I.copy}</button>` : ''}</div></label>
      ${this.slotViewsHtml(p, s)}
      ${s.note ? `<p class="whitespace-pre-line rounded-md bg-secondary p-2 text-xs">${esc(s.note)}</p>` : ''}
      <button class="${BTN_DANGER}" data-act="removeSlot">${I.trash}Remove from ${LABEL[k]}</button></div>`;
  }

  sendHtml(top) {
    const S = this.ui.send;
    if (S.step === 'done') {
      return `${top('')}<div class="flex flex-col items-center gap-4 p-6 text-center"><div class="text-2xl font-bold">${esc(S.label)}</div><p class="text-sm text-muted-foreground">${S.plats.map((k) => LABEL[k]).join(', ')}</p>
        <button class="${BTN_OUTLINE}" data-act="pickSendOut">Send another</button><button class="${BTN_PRIMARY} w-full" data-act="closeLayer">Done</button></div>`;
    }
    const whenLbl = { next: 'Next run', tonight: 'Tonight 10 PM', pick: 'Pick a time' };
    return `${top('Send out')}<div class="flex flex-col gap-4 overflow-y-auto p-4">
      <div class="flex items-center gap-3"><div class="w-16 shrink-0">${this.thumb({ title: S.file.name, kind: 'video', preview_url: S.preview, preview_type: S.file.type })}</div><b class="min-w-0 break-words">${esc(S.title || S.file.name)}</b></div>
      <p class="text-sm text-muted-foreground">Counts as posted on TikTok now.</p>
      <div class="flex flex-wrap gap-2">${CLAUDE.map((k) => `<button class="${BTN} h-10 rounded-full border px-3 ${S.plats.includes(k) ? 'border-primary bg-accent text-accent-foreground' : 'border-border'}" data-act="sendPlat" data-k="${k}" aria-pressed="${S.plats.includes(k)}">${dot(k)}${LABEL[k]}</button>`).join('')}</div>
      <div class="grid grid-cols-3 gap-1 rounded-lg bg-secondary p-1">${['next', 'tonight', 'pick'].map((w) => `<button class="rounded-md px-2 py-2 text-sm font-semibold ${S.when === w ? 'bg-background shadow-sm' : 'text-muted-foreground'}" data-act="sendWhen" data-k="${w}">${whenLbl[w]}</button>`).join('')}</div>
      ${S.when === 'pick' ? `<input type="datetime-local" class="${INPUT}" data-in="sendAt" value="${esc(S.at || '')}" aria-label="Time">` : ''}
      <input class="${INPUT}" data-in="sendYt" placeholder="YouTube title" value="${esc(S.yt || '')}" aria-label="YouTube title">
      <textarea rows="3" class="${INPUT}" data-in="sendCap" placeholder="Caption" aria-label="Caption">${esc(S.caption || '')}</textarea>
      <div class="text-sm text-muted-foreground" data-send-progress>${S.progress || ''}</div>
      <button class="${BTN_PRIMARY} h-14 text-lg" data-act="sendGo" ${S.sending ? 'disabled' : ''}>${I.ok}Send</button></div>`;
  }

  menuHtml(top) {
    const r = this.data_.rule;
    return `${top('Rule')}<div class="flex flex-col gap-4 overflow-y-auto p-4">
      <div class="grid grid-cols-2 gap-1 rounded-lg bg-secondary p-1">${CADENCES.map(([k, l]) => `<button class="rounded-md px-2 py-2 text-sm font-semibold ${r.cadence === k ? 'bg-background shadow-sm' : 'text-muted-foreground'}" data-act="cadence" data-k="${k}">${l}</button>`).join('')}</div>
      <div class="flex flex-col gap-1">${PL.map((k) => `<button class="flex h-11 items-center gap-3 rounded-md px-3 text-left hover:bg-accent" data-act="rulePlat" data-k="${k}">${dot(k)}<span class="flex-1">${LABEL[k]}</span><span class="text-xs text-muted-foreground">${k === 'tiktok' ? 'You' : 'Claude'}</span><span class="flex h-5 w-5 items-center justify-center rounded border ${r.platforms.includes(k) ? 'border-primary bg-primary text-primary-foreground' : 'border-input'}">${r.platforms.includes(k) ? svg('<path d="M20 6 9 17l-5-5"/>', 12) : ''}</span></button>`).join('')}</div>
      ${this.cancelsHtml()}</div>`;
  }

  linksHtml(top) {
    const rows = this.ui.links || [];
    const groups = [];
    rows.forEach((r) => { let g = groups.find((x) => x.item_id === r.item_id); if (!g) { g = { item_id: r.item_id, title: r.title, rows: [] }; groups.push(g); } g.rows.push(r); });
    return `${top('Shipped · 7 days')}<div class="flex flex-col gap-4 overflow-y-auto p-4">
      <button class="${BTN_PRIMARY}" data-act="copyAllLinks" ${rows.length ? '' : 'disabled'}>${I.copy}Copy all for email</button>
      ${groups.map((g) => `<section class="flex flex-col gap-1"><h3 class="font-semibold">${esc(g.title)}</h3>${g.rows.map((r) => `<div class="flex items-center gap-2 text-sm">${dot(r.platform)}<span class="w-20 shrink-0">${r.label}</span><span class="min-w-0 flex-1 truncate text-muted-foreground">${esc(r.url)}</span><button class="${BTN_ICON} h-8 w-8" data-act="copyText" data-text="${esc(r.url)}" aria-label="Copy">${I.copy}</button></div>`).join('')}</section>`).join('') || '<p class="text-center text-muted-foreground">Nothing posted this week</p>'}</div>`;
  }

  updateCount() {
    const c = this.layerTarget.querySelector('[data-count]');
    const it = this.item(this.ui.sel);
    if (!c || !it) return;
    const tab = this.ui.capTab;
    const txt = tab === 'default' ? (it.captions?.default || '') : (it.captions?.[tab] || it.captions?.default || '');
    const lim = tab === 'default' ? Math.min(...Object.values(LIMIT)) : LIMIT[tab];
    c.textContent = `${txt.length} / ${lim.toLocaleString()}`;
    c.classList.toggle('text-destructive', txt.length > lim);
  }

  // ---------- events ----------
  async click(e) {
    if (this.justDragged) { this.justDragged = false; return; }
    const t = e.target.closest('[data-act]');
    if (!t || !this.element.contains(t)) return;
    const d = t.dataset;
    const run = async (fn) => { try { await fn(); } catch (err) { this.toast(err.message); } };
    switch (d.act) {
      case 'play': {
        const v = t.querySelector('video');
        if (!v || v.controls) return; // already playing: let the native controls work
        t.querySelector('[data-play-icon]')?.remove();
        v.classList.replace('object-cover', 'object-contain');
        Object.assign(v, { controls: true, muted: false, currentTime: 0 });
        v.play().catch(() => {});
        return;
      }
      case 'closeLayer': this.closeLayer(); return;
      // Links inside a sheet or a redrawn card: the redraw removes the <a> before
      // the browser follows it, so navigate here instead.
      case 'go': e.preventDefault(); this.ui.report = null; this.closeLayer(); if (window.location.hash.slice(1) === d.to) this.render(); else window.location.hash = d.to; return;
      case 'dl': if (d.file && d.file !== 'undefined' && d.file !== 'null') { if (d.p) { this.ui.got.add(+d.p); this.renderView(); } run(() => this.download(d.file)); } else this.toast('No file yet'); return;
      case 'early': this.ui.early = true; this.renderView(); return;
      case 'reportDone': this.ui.report = null; this.renderView(); return;
      case 'openRule': this.openLayer('menu'); return;
      case 'pickUpload': this.closeLayer(); this.pickUpload(); return;
      case 'dlAll': run(async () => { for (const f of this.item(this.ui.sel).files) await this.download(f.id); }); return;
      case 'postedTT': run(() => this.postedTikTok(+d.p)); return;
      case 'ttOpen': this.ui.ttFor = +d.item; this.ui.ttAt = null; this.renderLayer(); return;
      case 'ttCancel': this.ui.ttFor = null; this.ui.ttAt = null; this.renderLayer(); return;
      case 'ttAgo': { const x = new Date(Date.now() - +d.m * 6e4); this.ui.ttAt = new Date(+x - x.getTimezoneOffset() * 6e4).toISOString().slice(0, 16); this.renderLayer(); return; }
      case 'ttSave': run(() => this.postedTikTokItem(+d.item)); return;
      // Off the TikTok queue; the item stays in the Library and nothing else gets it.
      case 'nextTT': run(async () => { await this.api('PATCH', `/posts/${d.p}/platforms/tiktok`, { move: 1 }); this.ui.got.delete(+d.p); await this.load(); this.toast('Next one up · that one comes right after'); }); return;
      case 'skipTT': run(async () => { await this.api('DELETE', `/posts/${d.p}/platforms/tiktok`); this.ui.got.delete(+d.p); await this.load(); this.toast('Skipped · still in the Library'); }); return;
      case 'delCard': run(async () => { const it = this.item(+d.item); if (!window.confirm(`Delete "${it.title}", its files and its posts?`)) return; const r = await this.apiAsk('DELETE', `/items/${it.id}`, null, 'delete'); if (!r) return; await this.load(); this.toastAfter(r, `Deleted${this.unschedText(r)}`); }); return;
      case 'copyCap': { const it = this.item(+d.item); const c = (d.k !== 'default' && it.captions?.[d.k]) || it.captions?.default || ''; if (!c) { this.flash(t, 'No caption yet'); return; } this.copy(c + (it.link ? `\n${it.link}` : '')); this.flash(t, 'Copied'); return; }
      case 'copyLink': { const it = this.item(+d.item); if (!it.link) { this.flash(t, 'No link yet'); return; } this.copy(it.link); this.flash(t, 'Copied'); return; }
      case 'copyText': this.copy(d.text); this.flash(t, 'Copied'); return;
      case 'openItem': this.ui.sel = +d.item; this.ui.capTab = 'default'; this.openLayer('item'); return;
      case 'kind': this.ui.kind = d.k; this.renderLibrary(); return;
      case 'phonePlat': this.ui.phonePlat = d.k; this.renderView(); return;
      case 'slot': this.ui.slot = [+d.p, d.k]; this.openLayer('slot'); return;
      case 'setState': run(() => this.patchSlot({ state: d.st })); return;
      case 'move': run(() => this.patchSlot({ move: +d.d })); return;
      case 'removeSlot': run(async () => { const [pid, k] = this.ui.slot; const r = await this.apiAsk('DELETE', `/posts/${pid}/platforms/${k}`, null, 'delete'); if (!r) return; this.closeLayer(); await this.load(); this.toastAfter(r, `Removed${this.unschedText(r)}`); }); return;
      case 'capTab': this.ui.capTab = d.k; this.renderLayer(); return;
      case 'addFile': this.fileMode = { mode: d.mode, replaces: d.file }; this.fileInputTarget.accept = d.mode === 'version' ? 'video/*' : ''; this.fileInputTarget.click(); return;
      case 'delFile': run(async () => { if (!window.confirm('Delete this file?')) return; await this.api('DELETE', `/files/${d.file}`); await this.load(); this.renderLayer(); this.toast('File deleted'); }); return;
      case 'delItem': run(async () => { const it = this.item(this.ui.sel); if (!window.confirm(`Delete "${it.title}", its files and its posts?`)) return; const r = await this.apiAsk('DELETE', `/items/${it.id}`, null, 'delete'); if (!r) return; this.closeLayer(); await this.load(); this.toastAfter(r, `Item deleted${this.unschedText(r)}`); }); return;
      case 'scheduleItem': run(async () => { await this.api('POST', '/schedule', { item_ids: [+d.item] }); await this.load(); this.renderLayer(); this.toast('Added to the plan'); }); return;
      case 'pickSendOut': this.pickSendOut(); return;
      case 'sendPlat': { const S = this.ui.send; S.plats = S.plats.includes(d.k) ? S.plats.filter((x) => x !== d.k) : CLAUDE.filter((k) => S.plats.includes(k) || k === d.k); this.renderLayer(); return; }
      case 'sendWhen': this.ui.send.when = d.k; this.renderLayer(); return;
      case 'sendGo': run(() => this.sendGo()); return;
      case 'cadence': run(() => this.saveRule({ ...this.data_.rule, cadence: d.k })); return;
      case 'rulePlat': { const r = this.data_.rule; const on = r.platforms.includes(d.k); if (on && r.platforms.length === 1) { this.toast('Keep one platform'); return; } run(() => this.saveRule({ ...r, platforms: on ? r.platforms.filter((x) => x !== d.k) : PL.filter((k) => r.platforms.includes(k) || k === d.k) })); return; }
      case 'openLinks': run(async () => { this.ui.links = (await this.api('GET', '/links')).links; this.openLayer('links'); }); return;
      case 'bookedPick': this.bookedPick(d.v); return;
      case 'copyAllLinks': { const groups = {}; (this.ui.links || []).forEach((r) => { (groups[r.title] = groups[r.title] || []).push(`${r.label}: ${r.url}`); }); this.copy(Object.entries(groups).map(([t, ls]) => `${t}\n${ls.join('\n')}`).join('\n\n')); this.toast(`${this.ui.links.length} links copied`); return; }
      default: if (d.act.startsWith('res')) this.resultsClick(d, run);
    }
  }

  input(e) {
    const t = e.target;
    if (t.matches('[data-lib-q]')) { this.ui.q = t.value; this.renderLibrary(); return; }
    const S = this.ui.send;
    if (t.matches('[data-tt-at]')) { this.ui.ttAt = t.value; return; }
    if (S && t.dataset.in?.startsWith('send')) { ({ sendAt: () => { S.at = t.value; }, sendYt: () => { S.yt = t.value; }, sendCap: () => { S.caption = t.value; } })[t.dataset.in]?.(); return; }
    const it = this.item(this.ui.sel);
    if (it && t.dataset.in === 'caption') {
      const tab = this.ui.capTab;
      it.captions = { ...(it.captions || {}) };
      if (t.value || tab === 'default') it.captions[tab] = t.value; else delete it.captions[tab];
      this.updateCount();
      this.saveItemSoon({ captions: { [tab]: t.value } });
    }
  }

  change(e) {
    const t = e.target;
    const it = this.item(this.ui.sel);
    const field = t.dataset.in;
    if (it && ['title', 'kind', 'link', 'yt_title', 'lane'].includes(field)) {
      it[field] = t.value;
      // A new lane can drop planned YouTube slots: reload the posts too.
      this.api('PATCH', `/items/${it.id}`, { [field]: t.value }).then(async () => { if (field === 'lane') await this.load(); this.toast('Saved'); this.renderView(); }).catch((err) => this.toast(err.message));
      return;
    }
    if (field === 'slotTime' && t.value) this.patchSlot({ scheduled_for: new Date(t.value).toISOString() }).catch((err) => this.toast(err.message));
    // Refused (a booked slot's link can't be cleared): show the link it kept.
    if (field === 'slotUrl') this.patchSlot({ url: t.value }).catch((err) => { this.renderLayer(); this.toast(err.message); });
    if (field === 'slotViews') this.saveSlotViews(t.value);
  }

  saveItemSoon(body) {
    const id = this.ui.sel;
    this.pendingCaption = { ...(this.pendingCaption || {}), ...body.captions };
    clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      const captions = this.pendingCaption; this.pendingCaption = null;
      this.api('PATCH', `/items/${id}`, { captions }).catch((err) => this.toast(err.message));
    }, 600);
  }

  async patchSlot(body) {
    const [pid, k] = this.ui.slot;
    const r = await this.apiAsk('PATCH', `/posts/${pid}/platforms/${k}`, body, body.state === 'planned' ? 'plan' : 'move');
    if (!r) return;
    await this.load();
    this.toastAfter(r, this.liveText(r) || (body.state ? `${LABEL[k]} · ${STATE_LABEL[body.state] || body.state}${this.unschedText(r)}` : 'Saved'));
  }

  async saveRule(rule) {
    this.data_.rule = (await this.api('PUT', '/rule', rule)).rule;
    this.renderLayer();
  }

  openEverything() { if (this.ui.layer === 'everything') this.closeLayer(); else this.openLayer('everything'); }

  // ---------- TikTok ----------
  // Optimistic: Home flips to "Posted ✓" + the cooldown at once; the save
  // happens behind it, and a failed save puts the card back.
  async postedTikTok(pid) {
    const s = this.slot(this.post(pid), 'tiktok');
    if (!s) return;
    const prev = { state: s.state, effective_state: s.effective_state, postedAt: s.postedAt };
    Object.assign(s, { state: 'posted', effective_state: 'posted', postedAt: new Date() });
    this.ui.early = false;
    this.ui.got.delete(pid);
    this.ui.justPosted = Date.now();
    this.renderView();
    try {
      await this.api('PATCH', `/posts/${pid}/platforms/tiktok`, { state: 'posted' });
    } catch (err) {
      Object.assign(s, prev); this.ui.justPosted = null; this.renderView();
      this.toast(`Didn't save: ${err.message}`);
      return;
    }
    this.toast(`Posted on TikTok · ${fmtT(new Date())}`, { label: 'Undo', fn: async () => { this.ui.justPosted = null; await this.api('PATCH', `/posts/${pid}/platforms/tiktok`, { state: 'planned' }); await this.load(); } });
    this.load();
  }

  // ---------- files ----------
  async download(fileId) {
    const { url } = await this.api('GET', `/files/${fileId}/download_link`);
    const a = document.createElement('a');
    a.href = url; a.rel = 'noopener';
    document.body.appendChild(a); a.click(); a.remove();
  }

  putFile(url, file, contentType, onProgress) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', url);
      xhr.setRequestHeader('Content-Type', contentType);
      xhr.upload.onprogress = (ev) => { if (ev.lengthComputable) onProgress?.(ev.loaded / ev.total); };
      xhr.onload = () => (xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`)));
      xhr.onerror = () => reject(new Error('Upload failed (network)'));
      xhr.send(file);
    });
  }

  // ---------- resumable uploads ----------
  // Every upload goes through sendFile. Up to 15 MB: one PUT. Bigger: R2
  // multipart in 8 MiB parts, 3 at a time, each retried with backoff and
  // paused while offline. It's remembered in localStorage by the file's name,
  // size and date, so picking the same file again sends only what R2 lacks.
  // create() starts the file on the server; ctx is what the upload is for, so
  // a file only resumes into the same place. Resolves { file, item }.
  // A one-PUT file can't resume: if it fails, an item it made goes again
  // (it has no posts yet), so picking the file again doesn't make a second
  // one. Its error says retry, not resume (err.retry).
  async sendFile(file, { ctx, itemId = null, create, extra = null }, onProgress) {
    if (file.size <= UP_MIN) {
      const r = await create();
      try {
        await this.putFile(r.upload_url, file, r.content_type, onProgress);
        return await this.api('POST', `/files/${r.file.id}/confirm`, { replaces: r.replaces });
      } catch (err) {
        if (r.item?.id) await this.api('DELETE', `/items/${r.item.id}?unschedule=false`).catch(() => {});
        throw Object.assign(err, { retry: true });
      }
    }
    this.upActive = (this.upActive || 0) + 1;
    if (this.upActive === 1) {
      this.upXhrs = new Set();
      this.onOffline = () => this.upXhrs.forEach((x) => x.abort()); // fail fast, then wait for 'online'
      window.addEventListener('offline', this.onOffline);
    }
    try {
      let up = this.upSaved(file, ctx);
      let m = null;
      if (up) {
        try {
          m = await this.upApi('POST', `/files/${up.fileId}/multipart`);
          if (m.parts_done?.length) this.toast('Resuming upload');
        } catch (err) {
          if (err.flaky) throw err; // still offline: keep it for next time
          this.upForget(file, ctx); up = null; // file gone: start over
        }
      }
      if (!up) {
        const r = await create();
        up = { fileId: r.file.id, itemId: r.item?.id ?? itemId, ctx, replaces: r.replaces ?? null };
        this.upRemember(file, up);
        m = await this.upApi('POST', `/files/${up.fileId}/multipart`);
      } else if (extra && up.itemId) {
        // Resumed Send out: keep the caption typed this time.
        const body = Object.fromEntries(Object.entries(extra).filter(([, v]) => v));
        if (Object.keys(body).length) await this.api('PATCH', `/items/${up.itemId}`, body);
      }
      if (!m.uploaded) {
        const parts = await this.sendParts(file, up.fileId, m, onProgress);
        m = await this.upApi('POST', `/files/${up.fileId}/multipart/complete`, { parts, replaces: up.replaces });
      }
      this.upForget(file, ctx);
      return { file: m.file, item: m.item };
    } finally {
      this.upActive -= 1;
      if (!this.upActive) window.removeEventListener('offline', this.onOffline);
    }
  }

  // Sends the parts R2 doesn't have. Resolves the part list for complete, or
  // null when an ETag couldn't be read (the server then asks R2).
  async sendParts(file, fileId, m, onProgress) {
    const ps = m.part_size;
    const len = (i) => Math.min(ps, file.size - (i - 1) * ps);
    const etags = {};
    (m.parts_done || []).forEach((p) => { if (p.size === len(p.part_number)) etags[p.part_number] = p.etag; });
    const todo = [];
    for (let i = 1; i <= m.part_count; i += 1) if (!(i in etags)) todo.push(i);
    const sending = {};
    let sent = Object.keys(etags).reduce((s, i) => s + len(+i), 0);
    const tick = () => { if (navigator.onLine !== false) onProgress?.((sent + Object.values(sending).reduce((a, b) => a + b, 0)) / file.size); };
    // Upload links, fetched 50 at a time; one fetch at once covers every
    // part waiting on a link, so a wait never takes more than two fetches.
    const urls = {};
    const waiting = new Set();
    let fetching = null;
    const urlFor = async (i) => {
      waiting.add(i);
      try {
        for (let n = 0; n < 4; n += 1) {
          const u = urls[i];
          if (u && Date.now() - u.at < 45 * 6e4) return u.url; // links last an hour
          if (!fetching) {
            const want = [...new Set([...waiting, ...todo.filter((j) => !urls[j])])].slice(0, 50);
            fetching = this.upApi('GET', `/files/${fileId}/multipart/parts?numbers=${want.join(',')}`)
              .then((r) => { const at = Date.now(); Object.entries(r.urls).forEach(([k, url]) => { urls[k] = { url, at }; }); })
              .finally(() => { fetching = null; });
          }
          await fetching;
        }
      } finally { waiting.delete(i); }
      throw Object.assign(new Error(`no link for part ${i}`), { hopeless: true });
    };
    // One job per upload: a part that won't go stops the others too.
    const job = { failed: null, xhrs: new Set(), urlFor, bytes: (i, b) => { sending[i] = b; tick(); }, drop: (i) => { delete urls[i]; } };
    const worker = async () => {
      while (todo.length && !job.failed) {
        const i = todo.shift();
        const at = (i - 1) * ps;
        try {
          etags[i] = await this.sendPart(file.slice(at, at + len(i)), i, job);
        } catch (err) {
          if (!job.failed) { job.failed = err; job.xhrs.forEach((x) => x.abort()); }
          throw job.failed;
        }
        delete sending[i]; sent += len(i); tick();
      }
    };
    tick();
    await Promise.all(Array.from({ length: Math.min(UP_PARALLEL, todo.length) }, worker));
    const nums = Object.keys(etags).map(Number).sort((a, b) => a - b);
    return nums.every((i) => etags[i]) ? nums.map((i) => ({ part_number: i, etag: etags[i] })) : null;
  }

  // One part, retried up to UP_RETRIES times with backoff. Time offline
  // doesn't count: it waits for 'online' and tries again.
  async sendPart(blob, i, job) {
    let tries = 0;
    for (;;) {
      await this.upOnline();
      if (job.failed) throw job.failed;
      try {
        return await this.putPart(await job.urlFor(i), blob, (b) => job.bytes(i, b), job.xhrs);
      } catch (err) {
        job.bytes(i, 0);
        if (job.failed) throw job.failed;
        if (err.status === 403) job.drop(i); // link expired: get a fresh one
        const hopeless = err.hopeless || (err.status >= 400 && err.status < 500 && ![403, 408, 429].includes(err.status));
        if (navigator.onLine === false && !hopeless) continue;
        tries += 1;
        if (hopeless || tries > UP_RETRIES) throw new Error(`Upload stopped (${err.message}). Pick the same file again to resume.`);
        await sleep(backoff(tries - 1));
      }
    }
  }

  // PUT one part straight to R2. Resolves its ETag (null if CORS hides it).
  putPart(url, blob, onBytes, xhrs) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      let stall = null;
      const arm = () => { clearTimeout(stall); stall = setTimeout(() => xhr.abort(), 60000); }; // a minute with no bytes: try again
      const end = () => { clearTimeout(stall); xhrs.delete(xhr); this.upXhrs?.delete(xhr); };
      const fail = (msg, status) => { end(); reject(Object.assign(new Error(msg), { status })); };
      xhr.open('PUT', url);
      xhr.upload.onprogress = (ev) => { arm(); onBytes(ev.loaded); };
      xhr.onload = () => {
        if (xhr.status >= 300) { fail(`HTTP ${xhr.status}`, xhr.status); return; }
        end();
        resolve(xhr.getResponseHeader('ETag'));
      };
      xhr.onerror = () => fail('network error');
      xhr.onabort = () => fail('connection lost');
      xhrs.add(xhr);
      this.upXhrs?.add(xhr);
      arm();
      xhr.send(blob);
    });
  }

  // this.api, retried through network drops and server hiccups (5xx).
  async upApi(method, path, body) {
    let tries = 0;
    for (;;) {
      await this.upOnline();
      try {
        return await this.api(method, path, body);
      } catch (err) {
        const flaky = err instanceof TypeError || err.status >= 500 || err.status === 408 || err.status === 429;
        if (flaky && navigator.onLine === false) continue; // offline: wait, doesn't count
        if (!flaky || tries >= UP_RETRIES) throw Object.assign(err, { hopeless: true, flaky });
        tries += 1;
        await sleep(backoff(tries - 1));
      }
    }
  }

  // Resolves now, or once the browser is back online.
  upOnline() {
    if (navigator.onLine !== false) return Promise.resolve();
    if (!this.upWait) {
      this.toast(`Offline. The upload resumes when you're back.`, null, true);
      this.upWait = new Promise((resolve) => {
        window.addEventListener('online', () => { this.upWait = null; this.toast('Back online, uploading'); resolve(); }, { once: true });
      });
    }
    return this.upWait;
  }

  upAll() {
    try { return JSON.parse(window.localStorage.getItem(UP_STORE)) || {}; } catch (_) { return {}; }
  }

  upPut(all) {
    try { window.localStorage.setItem(UP_STORE, JSON.stringify(all)); } catch (_) { /* private mode: no resume */ }
  }

  // Same file for the same purpose. A name+size match counts too: iOS can
  // re-date a re-picked video. Only within UP_RESUME.
  upSaved(file, ctx) {
    const all = this.upAll();
    const hit = all[upKey(file, ctx)] || Object.values(all).find((u) => this.upSame(u, file, ctx));
    return hit && Date.now() - hit.at < UP_RESUME ? hit : null;
  }

  upSame(u, file, ctx) { return u.name === file.name && u.size === file.size && u.ctx === ctx; }

  upRemember(file, up) {
    const all = this.upAll();
    Object.keys(all).forEach((k) => { if (!(Date.now() - all[k].at < UP_RESUME)) delete all[k]; });
    all[upKey(file, up.ctx)] = { ...up, name: file.name, size: file.size, at: Date.now() };
    this.upPut(all);
  }

  upForget(file, ctx) {
    const all = this.upAll();
    Object.keys(all).forEach((k) => { if (this.upSame(all[k], file, ctx)) delete all[k]; });
    this.upPut(all);
  }

  // On load: name the uploads that didn't finish, so they get picked again.
  upHint() {
    const left = Object.values(this.upAll()).filter((u) => Date.now() - u.at < UP_RESUME);
    if (!left.length) return;
    this.toast(left.length > 1 ? `${left.length} uploads didn't finish. Pick them again to resume.` : `${left[0].name} didn't finish uploading. Pick it again to resume.`, { label: 'OK', fn: () => {} });
  }

  // New item from a file, uploaded straight to R2.
  async uploadNew(file, extra = {}, onProgress) {
    const create = () => this.api('POST', '/items', { filename: file.name, content_type: file.type, byte_size: file.size, ...extra });
    return (await this.sendFile(file, { ctx: 'new', create, extra }, onProgress)).item;
  }

  pickUpload() { this.uploadInputTarget.click(); }

  async uploadPicked(e) { const files = [...e.target.files]; e.target.value = ''; await this.uploadBatch(files); }

  // Header Upload / drop: every file becomes an item, scheduled by the rule.
  // A file that fails doesn't stop the rest: what finished is still scheduled,
  // and the toast names only the files to pick again: a big one resumes into
  // its own item; a small one (one PUT) left nothing behind and starts over.
  // Either way nothing that finished is uploaded twice.
  async uploadBatch(files) {
    if (!files.length) return;
    this.ui.busy += 1;
    const done = [];
    const failed = [];
    try {
      for (const [i, f] of files.entries()) {
        const label = files.length > 1 ? `${i + 1}/${files.length} ` : '';
        try {
          const item = await this.uploadNew(f, {}, (p) => this.toast(`Uploading ${label}${Math.round(p * 100)}%`, null, true));
          done.push({ id: item.id, media: /^(video|image)\//.test(f.type) });
        } catch (err) {
          failed.push({ name: f.name, retry: !!err.retry });
        }
      }
      const media = done.filter((d) => d.media).map((d) => d.id);
      if (media.length) await this.api('POST', '/schedule', { item_ids: media });
      await this.load();
      const ok = media.length ? `${media.length} queued` : `${done.length} uploaded`;
      if (failed.length) {
        const again = this.failedText(failed);
        this.toast(done.length ? `${ok} · ${again}` : again, { label: 'OK', fn: () => {} }, true);
      } else if (media.length && this.route() === 'home') {
        // Home: the batch receipt card shows where it all landed.
        this.toast(ok);
        this.ui.report = { type: 'batch', itemIds: media };
        if (this.hasViewTarget) this.renderView();
      } else {
        this.toast(ok, media.length ? { label: 'See in Plan', fn: () => { window.location.hash = 'plan'; } } : null);
      }
    } catch (err) {
      this.toast(err.message);
      this.load();
    } finally {
      this.ui.busy -= 1;
    }
  }

  // What to pick again: small files start over ("retry"), big ones resume.
  failedText(failed) {
    const names = (list) => list.map((x) => x.name).join(', ');
    const retry = failed.filter((x) => x.retry);
    const resume = failed.filter((x) => !x.retry);
    return [
      retry.length ? `${names(retry)} didn't upload. Pick ${retry.length > 1 ? 'them' : 'it'} again to retry.` : '',
      resume.length ? `${names(resume)} didn't finish. Pick just ${resume.length > 1 ? 'those files' : 'that file'} again to resume.` : '',
    ].filter(Boolean).join(' ');
  }

  async filePicked(e) {
    const file = e.target.files[0];
    e.target.value = '';
    const it = this.item(this.ui.sel);
    if (!file || !it) return;
    const { mode, replaces } = this.fileMode || {};
    try {
      const create = () => this.api('POST', `/items/${it.id}/files`, { filename: file.name, content_type: file.type, byte_size: file.size, version: mode === 'version', replaces: mode === 'replace' ? replaces : null });
      await this.sendFile(file, { ctx: `item:${it.id}:${mode}:${mode === 'replace' ? replaces : ''}`, itemId: it.id, create }, (p) => this.toast(`Uploading ${Math.round(p * 100)}%`, null, true));
      if (mode === 'version' && window.confirm('Add this version to the plan?')) await this.api('POST', '/schedule', { item_ids: [it.id] });
      await this.load();
      this.renderLayer();
      this.toast({ version: 'New version added', replace: 'File replaced' }[mode] || 'File added');
    } catch (err) { this.toast(err.message); }
  }

  // ---------- send out (phone: one video already on TikTok) ----------
  pickSendOut() { this.sendInputTarget.click(); }

  sendPicked(e) {
    const files = [...e.target.files];
    e.target.value = '';
    this.startSend(files);
  }

  startSend(files) {
    if (!files.length) return;
    if (files.length > 1) { this.uploadBatch(files); return; }
    const file = files[0];
    const plats = CLAUDE.filter((k) => this.data_.rule.platforms.includes(k));
    let preview = null;
    try { preview = URL.createObjectURL(file); } catch (_) { /* no preview */ }
    this.ui.send = { step: 'confirm', file, preview, plats: plats.length ? plats : ['youtube', 'facebook'], when: 'next', title: file.name.replace(/\.[a-z0-9]+$/i, '') };
    this.openLayer('send');
  }

  async sendGo() {
    const S = this.ui.send;
    if (!S.plats.length) { this.toast('Pick a platform'); return; }
    if (S.when === 'pick' && !S.at) { this.toast('Pick a time'); return; }
    S.sending = true; this.renderLayer();
    try {
      const item = await this.uploadNew(S.file, { caption: S.caption, yt_title: S.yt }, (p) => {
        S.progress = `Uploading ${Math.round(p * 100)}%`;
        const el = this.layerTarget.querySelector('[data-send-progress]'); if (el) el.textContent = S.progress;
      });
      await this.api('POST', `/items/${item.id}/send_out`, { platforms: S.plats, when: S.when, at: S.at ? new Date(S.at).toISOString() : null });
      this.ui.report = { type: 'sent', itemId: item.id };
      this.closeLayer();
      if (this.route() !== 'home') window.location.hash = 'home';
      await this.load();
      return;
    } catch (err) {
      S.sending = false; S.progress = err.message;
    }
    this.renderLayer();
  }

  // ---------- drop anywhere (desktop) ----------
  bindDrop() {
    let depth = 0;
    const hasFiles = (e) => [...(e.dataTransfer?.types || [])].includes('Files');
    const show = (on) => { this.dropTarget.classList.toggle('hidden', !on); this.dropTarget.classList.toggle('flex', on); };
    this.onDragEnter = (e) => { if (!hasFiles(e)) return; e.preventDefault(); depth += 1; show(true); };
    this.onDragLeave = (e) => { if (!hasFiles(e)) return; depth = Math.max(0, depth - 1); if (!depth) show(false); };
    this.onDragOver = (e) => { if (hasFiles(e)) e.preventDefault(); };
    this.onDrop = (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault(); depth = 0; show(false);
      const files = [...e.dataTransfer.files];
      if (this.ui.layer === 'item') { this.fileMode = { mode: 'file' }; this.filePicked({ target: { files, value: '' } }); return; }
      if (this.route() === 'home') this.startSend(files); else this.uploadBatch(files);
    };
    window.addEventListener('dragenter', this.onDragEnter);
    window.addEventListener('dragleave', this.onDragLeave);
    window.addEventListener('dragover', this.onDragOver);
    window.addEventListener('drop', this.onDrop);
  }

  // ---------- drag in Plan (mouse: drag; touch: long-press then drag) ----------
  bindDrag() {
    const PH = { youtube: 22, facebook: 19, instagram: 20, linkedin: 9, x: 13 };
    let st = null;
    const cellAt = (x, y) => document.elementFromPoint(x, y)?.closest('[data-cell]');
    const mark = (cell) => {
      if (st.over === cell) return;
      st.over?.classList.remove('ring-2', 'ring-primary', 'ring-inset', 'ring-destructive');
      st.over = cell;
      if (cell) cell.classList.add('ring-2', 'ring-inset', this.dropOk(st, cell) ? 'ring-primary' : 'ring-destructive');
    };
    const begin = () => {
      st.active = true;
      const r = st.el.getBoundingClientRect();
      st.dx = st.x0 - r.left; st.dy = st.y0 - r.top;
      st.ghost = st.el.cloneNode(true);
      Object.assign(st.ghost.style, { position: 'fixed', left: `${r.left}px`, top: `${r.top}px`, width: `${r.width}px`, zIndex: 60, pointerEvents: 'none', boxShadow: '0 8px 24px rgb(0 0 0 / .18)', transform: 'rotate(-2deg)' });
      document.body.appendChild(st.ghost);
      st.el.classList.add('opacity-30');
      document.body.style.userSelect = 'none';
      navigator.vibrate?.(15);
    };
    const end = async (drop) => {
      if (!st) return;
      const s0 = st; st = null;
      clearTimeout(s0.timer);
      document.body.style.userSelect = '';
      s0.ghost?.remove();
      s0.el.classList.remove('opacity-30');
      s0.over?.classList.remove('ring-2', 'ring-primary', 'ring-inset', 'ring-destructive');
      if (!s0.active) return;
      this.justDragged = true;
      setTimeout(() => { this.justDragged = false; }, 50);
      if (drop && s0.over) await this.dropSlot(s0, s0.over, PH);
    };
    this.element.addEventListener('pointerdown', (e) => {
      const el = e.target.closest('[data-drag]');
      if (!el || e.button > 0 || this.route() !== 'plan') return;
      st = { el, p: +el.dataset.p, k: el.dataset.k, x0: e.clientX, y0: e.clientY, touch: e.pointerType !== 'mouse', active: false, over: null };
      if (st.touch) st.timer = setTimeout(() => { if (st) begin(); }, 350);
    });
    window.addEventListener('pointermove', (e) => {
      if (!st) return;
      const far = Math.abs(e.clientX - st.x0) + Math.abs(e.clientY - st.y0) > 6;
      if (!st.active) {
        if (st.touch) { if (far) end(false); return; } // a scroll, not a drag
        if (!far) return;
        begin();
      }
      e.preventDefault();
      st.ghost.style.left = `${e.clientX - st.dx}px`;
      st.ghost.style.top = `${e.clientY - st.dy}px`;
      st.lastX = e.clientX; st.lastY = e.clientY;
      mark(cellAt(e.clientX, e.clientY));
    }, { passive: false });
    window.addEventListener('pointerup', () => end(true));
    window.addEventListener('pointercancel', () => end(false));
    // While a touch drag is active, stop the page from scrolling.
    window.addEventListener('touchmove', (e) => { if (st?.active) e.preventDefault(); }, { passive: false });
  }

  dropOk(st, cell) {
    const [k, o] = cell.dataset.cell.split('|');
    if (o === 'Q') return true;
    return k !== 'tiktok' && +o >= 0;
  }

  async dropSlot(st, cell, PH) {
    const [toK, o] = cell.dataset.cell.split('|');
    const p = this.post(st.p);
    const s = p && this.slot(p, st.k);
    if (!s) return;
    if (!this.dropOk(st, cell)) { this.toast(toK === 'tiktok' ? 'Drop it in the TikTok queue' : 'Pick a future day'); return; }
    const body = { to_platform: toK };
    if (o === 'Q') {
      // place = before the chip under the pointer, else the end
      const chips = [...cell.querySelectorAll('[data-k="tiktok"][data-drag]')].filter((c) => c !== st.el);
      const under = document.elementFromPoint(st.lastX, st.lastY)?.closest('[data-drag]');
      const q = this.ttQueue().filter((x) => x.p.id !== st.p || st.k !== 'tiktok');
      let place = q.length + 1;
      if (under && chips.includes(under)) place = q.findIndex((x) => x.p.id === +under.dataset.p) + 1;
      else if (cell.dataset.cell === 'tiktok|Q' && isPhone()) {
        const rows = [...this.viewTarget.querySelectorAll('[data-cell="tiktok|Q"]')];
        place = rows.indexOf(cell) + 1;
      }
      body.place = place;
    } else {
      const day = addDays(dayStart(new Date()), +o);
      const keep = toK === st.k && s.when;
      day.setHours(keep ? s.when.getHours() : PH[toK], keep ? s.when.getMinutes() : 0, 0, 0);
      if (toK === st.k && s.when && offOf(s.when) === +o) return;
      body.scheduled_for = day.toISOString();
    }
    try {
      const r = await this.apiAsk('POST', `/posts/${st.p}/platforms/${st.k}/move`, body, 'move');
      if (!r) return;
      await this.load();
      this.toastAfter(r, this.liveText(r) || (o === 'Q' ? `TikTok #${body.place}` : `${LABEL[toK]} · ${fmtDay(new Date(body.scheduled_for))} ${fmtT(new Date(body.scheduled_for))}`));
    } catch (err) { this.toast(err.message); }
  }

  // ---------- stats: counts per platform, and what got booked or posted lately ----------
  renderStats() {
    if (!this.stats) this.stats = { data: null, at: 0, loading: false, error: null };
    const S = this.stats;
    if (Date.now() - S.at > 6e4 && !S.loading) this.loadStats();
    this.viewTarget.innerHTML = this.statsHtml();
  }

  async loadStats() {
    const S = this.stats;
    S.loading = true; S.error = null;
    try { S.data = await this.api('GET', '/overview'); S.at = Date.now(); } catch (e) { S.error = e.message; } finally { S.loading = false; }
    if (this.route() === 'stats') this.viewTarget.innerHTML = this.statsHtml();
  }

  statsHtml() {
    const S = this.stats;
    const d = S.data;
    if (!d) return `<p class="py-10 text-center text-muted-foreground">${S.error ? `Couldn't load stats: ${esc(S.error)}` : 'Loading…'}</p>`;
    const when = (t) => { if (!t) return '-'; const x = new Date(t); return x <= new Date() ? 'Now' : `${fmtDay(x)} ${fmtT(x)}`; };
    const day = (t) => (t ? fmtDay(new Date(t)) : '-');
    const ps = d.platforms;
    const row = (label, f, hint = '') => `<tr class="border-b border-border last:border-0"><th scope="row" class="py-2.5 pr-3 text-left text-sm font-medium">${label}${hint ? `<span class="block text-xs font-normal text-muted-foreground">${hint}</span>` : ''}</th>${ps.map((p) => `<td class="px-2 py-2.5 text-right text-sm tabular-nums">${f(p)}</td>`).join('')}</tr>`;
    const v = d.videos;
    const tile = (n, label) => `<div class="flex flex-col rounded-xl border border-border bg-background p-3"><b class="text-2xl font-bold tabular-nums">${fmtN(n)}</b><span class="text-xs text-muted-foreground">${label}</span></div>`;
    const l24 = ps.map((p) => {
      const x = d.last_24h[p.platform];
      const bits = [p.platform !== 'tiktok' && x.booked ? `${x.booked} booked` : '', x.posted ? `${x.posted} posted` : ''].filter(Boolean);
      return `<span class="inline-flex items-center gap-1">${dot(p.platform)}${LABEL[p.platform]} ${bits.join(', ') || 'nothing'}</span>`;
    }).join('');
    const ev = d.recent.map((r) => `<li class="flex items-center gap-2 py-2 text-sm">${dot(r.platform)}<span class="w-24 shrink-0 text-xs text-muted-foreground">${fmtDay(new Date(r.at))} ${fmtT(new Date(r.at))}</span><span class="min-w-0 flex-1 truncate">${r.what === 'booked' ? `Booked on ${LABEL[r.platform]} for ${r.scheduled_for ? `${fmtDay(new Date(r.scheduled_for))} ${fmtT(new Date(r.scheduled_for))}` : '?'}` : `Posted on ${LABEL[r.platform]}`} · ${esc(r.title)}</span>${/^https?:\/\//.test(r.url || '') ? `<a class="text-xs text-muted-foreground underline hover:text-foreground" href="${esc(r.url)}" target="_blank" rel="noopener">Open</a>` : ''}</li>`).join('');
    return `<div class="mx-auto flex max-w-3xl flex-col gap-5 px-3 pt-4 pb-24">
      <div class="grid grid-cols-3 gap-2">${tile(v.total, 'videos in the DAM')}${tile(v.added_7d, 'added this week')}${tile(v.added_24h, 'added today')}</div>
      <section class="rounded-xl border border-border bg-background p-3"><h2 class="mb-1 font-semibold">Last 24 hours</h2><p class="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">${l24}</p></section>
      <section class="overflow-x-auto rounded-xl border border-border bg-background p-3"><table class="w-full min-w-[420px]">
        <thead><tr class="border-b border-border"><th></th>${ps.map((p) => `<th scope="col" class="px-2 pb-2 text-right text-sm font-semibold"><span class="inline-flex items-center gap-1">${dot(p.platform)}${LABEL[p.platform]}</span></th>`).join('')}</tr></thead>
        <tbody>
          ${row('Not planned yet', (p) => fmtN(p.not_planned), 'videos with no slot there')}
          ${row('Waiting', (p) => fmtN(p.planned), 'Planned, or in the TikTok queue')}
          ${row('Booked', (p) => (p.platform === 'tiktok' ? '-' : fmtN(p.scheduled)), 'Scheduled there, not live yet')}
          ${row('Posted this week', (p) => fmtN(p.posted_7d))}
          ${row('Posted, all time', (p) => fmtN(p.posted_total))}
          ${row('Next one', (p) => when(p.next_at))}
          ${row('Booked through', (p) => (p.platform === 'tiktok' ? '-' : day(p.booked_through)))}
        </tbody></table></section>
      <section class="rounded-xl border border-border bg-background p-3"><h2 class="font-semibold">Lately</h2><p class="text-xs text-muted-foreground">Bookings and posts in the last 7 days. Facebook is booked by the server every 10 minutes, a week ahead.</p>
        <ul class="divide-y divide-border">${ev || '<li class="py-3 text-sm text-muted-foreground">Nothing yet</li>'}</ul></section>
    </div>`;
  }

  // ---------- results: how the videos did (numbers only; reposting is Andrew's call) ----------
  resState() {
    if (!this.res) this.res = { days: 7, data: null, at: 0, loading: false, error: null, refreshing: false, sort: 'views', dir: -1, slotStats: {} };
    return this.res;
  }

  renderResults() {
    const R = this.resState();
    const fresh = R.data && R.data.period.days === R.days && Date.now() - R.at < 5 * 6e4;
    if (!fresh && !R.loading && !R.error) this.loadResults();
    this.viewTarget.innerHTML = this.resultsHtml();
  }

  async loadResults() {
    const R = this.resState();
    R.loading = true; R.error = null;
    const days = R.days;
    try {
      const d = await this.api('GET', `/results?days=${days}`);
      if (days === R.days) { R.data = d; R.at = Date.now(); }
    } catch (e) {
      R.error = e.message;
    } finally {
      R.loading = false;
    }
    // Draws, or fetches again if the period changed while this was loading.
    if (this.route() === 'results') this.renderResults();
  }

  resultsHtml() {
    const R = this.resState();
    const d = R.data && R.data.period.days === R.days ? R.data : null;
    const at = d?.collected_at ? new Date(d.collected_at) : null;
    let h = `<div class="mx-auto flex max-w-6xl flex-col gap-5 px-3 pt-4">
      <div class="flex flex-wrap items-center gap-2">
        <div class="grid w-full grid-cols-3 gap-1 rounded-lg bg-secondary p-1 sm:w-auto" role="group" aria-label="Period">${RES_DAYS.map((n) => `<button class="rounded-md px-3 py-1.5 text-sm font-semibold ${R.days === n ? 'bg-background shadow-sm' : 'text-muted-foreground'}" data-act="resDays" data-k="${n}" aria-pressed="${R.days === n}">${n} days</button>`).join('')}</div>
        <div class="flex-1"></div>
        <span class="text-xs text-muted-foreground">${at ? `Updated ${dayWord(at)}, ${fmtT(at)}` : d ? 'Not collected yet' : ''}</span>
        <button class="${BTN_OUTLINE} h-9" data-act="resRefresh" ${R.refreshing ? 'disabled' : ''}>${RES_ICON.refresh}${R.refreshing ? 'Refreshing…' : 'Refresh numbers'}</button>
      </div>`;
    if (!d) {
      h += R.error
        ? `<div class="flex flex-col items-center gap-3 rounded-xl border border-border bg-background p-8 text-center text-muted-foreground">Couldn't load results: ${esc(R.error)}<button class="${BTN_OUTLINE}" data-act="resRetry">Try again</button></div>`
        : '<p class="py-10 text-center text-muted-foreground">Loading…</p>';
      return `${h}</div>`;
    }
    h += this.resTotalsHtml(d) + this.resWinnersHtml(d) + this.resRowsHtml(d);
    return `${h}</div>`;
  }

  // Headline numbers per platform, with the change against the period before.
  resTotalsHtml(d) {
    const keys = PL.filter((k) => d.totals[k] || d.previous_totals[k]);
    if (!keys.length) return `<p class="rounded-xl border border-border bg-background p-8 text-center text-muted-foreground">Nothing posted in the last ${d.period.days} days</p>`;
    return `<div class="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">${keys.map((k) => {
      const t = d.totals[k] || { posts: 0, tracked: 0, views: null };
      const p = d.previous_totals[k];
      const extra = [t.likes != null ? nWord(t.likes, 'like') : '', t.comments != null ? nWord(t.comments, 'comment') : ''].filter(Boolean).join(' · ');
      const note = k === 'tiktok' ? 'No views typed in' : 'No numbers';
      return `<div class="flex flex-col gap-0.5 rounded-xl border border-border bg-background p-3">
        <div class="flex items-center gap-1.5 text-sm font-semibold">${dot(k)}${LABEL[k]}</div>
        <div class="text-2xl font-bold tabular-nums">${t.views == null ? '<span class="text-muted-foreground">-</span>' : compactN(t.views)}</div>
        <div class="text-xs text-muted-foreground">${t.views == null ? note : 'views'} · ${t.posts} post${t.posts === 1 ? '' : 's'}</div>
        ${extra ? `<div class="text-xs text-muted-foreground">${extra}</div>` : ''}
        <div class="mt-1 text-xs">${this.resChange(t, p, d.period.days)}</div>
      </div>`;
    }).join('')}</div>`;
  }

  resChange(t, p, days) {
    const before = `<span class="text-muted-foreground">vs the ${days} days before</span>`;
    if (t.views != null && p?.views != null) {
      const diff = t.views - p.views;
      const txt = p.views > 0 ? `${diff >= 0 ? '+' : ''}${Math.round((100 * diff) / p.views)}%` : `+${compactN(diff)}`;
      return `<span class="font-semibold ${diff > 0 ? 'text-emerald-700' : diff < 0 ? 'text-destructive' : ''}">${txt}</span> ${before}`;
    }
    const posts = p?.posts || 0;
    return `<span class="text-muted-foreground">${posts} post${posts === 1 ? '' : 's'} the ${days} days before</span>`;
  }

  resWinnersHtml(d) {
    if (!d.winners.length) {
      return d.rows.length ? '<p class="rounded-xl border border-border bg-background p-6 text-center text-sm text-muted-foreground">No winners yet. YouTube and Facebook numbers come in every 6 hours.</p>' : '';
    }
    return `<section class="flex flex-col gap-2"><h2 class="text-lg font-bold">Winners</h2>
      <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">${d.winners.map((w, i) => this.resWinnerHtml(w, i)).join('')}</div></section>`;
  }

  resWinnerHtml(w, i) {
    const it = this.item(w.item.id) || w.item;
    const best = w.platforms[0];
    return `<article class="flex flex-col gap-3 rounded-xl border border-border bg-background p-3 shadow-sm">
      <div class="flex gap-3">
        <div class="w-20 shrink-0 sm:w-24">${this.thumb(it, 'w-full', '9/16', true)}</div>
        <div class="flex min-w-0 flex-1 flex-col gap-1">
          <div class="flex items-start gap-2"><span class="text-xl font-bold leading-tight text-muted-foreground">${i + 1}</span>
            <button class="min-w-0 break-words text-left text-base font-bold leading-tight hover:underline" data-act="openItem" data-item="${it.id}">${esc(it.title)}</button></div>
          <div class="text-xs text-muted-foreground">${w.score.toFixed(1)}x the ${LABEL[best.platform]} median</div>
          <button class="${BTN_OUTLINE} mt-auto w-full" data-act="resAgain" data-item="${it.id}">${I.clock}Schedule again</button>
        </div>
      </div>
      <div class="flex flex-col divide-y divide-border rounded-lg border border-border">${w.platforms.map((s) => `
        <div class="flex min-h-[40px] items-center gap-2 pl-3 pr-1 text-sm">${dot(s.platform)}<span class="min-w-0 flex-1 truncate">${LABEL[s.platform]}</span>
          <span class="whitespace-nowrap tabular-nums">${s.views == null ? '<span class="text-muted-foreground">No numbers</span>' : `<b>${fmtN(s.views)}</b> <span class="text-muted-foreground">views</span>`}</span>
          ${this.resLinkHtml(s)}</div>`).join('')}</div>
    </article>`;
  }

  resLinkHtml(r) {
    if (!r.url) return '<span class="w-8 shrink-0"></span>';
    return `<a href="${esc(r.url)}" target="_blank" rel="noopener" class="${BTN} h-8 w-8 shrink-0 text-muted-foreground hover:bg-accent hover:text-accent-foreground" data-act="resLink" aria-label="Open on ${LABEL[r.platform]}" title="Open on ${LABEL[r.platform]}">${RES_ICON.ext}</a>`;
  }

  // Every posted video: a sortable table on desktop, a list on the phone.
  resRowsHtml(d) {
    if (!d.rows.length) return '';
    const R = this.resState();
    const val = (r) => {
      if (R.sort === 'title') return (r.title || '').toLowerCase();
      if (R.sort === 'platform') return PL.indexOf(r.platform);
      if (R.sort === 'posted_at') return +new Date(r.posted_at);
      return r[R.sort];
    };
    const rows = [...d.rows].sort((a, b) => {
      const x = val(a); const y = val(b);
      if (x == null || y == null) return (x == null) - (y == null); // no numbers go last
      return (x < y ? -1 : x > y ? 1 : 0) * R.dir;
    });
    const views = (r) => (r.views == null && r.platform === 'tiktok' ? '<span class="text-primary">Type views</span>' : fmtN(r.views));
    const score = (r) => (r.score == null ? '-' : `${r.score.toFixed(1)}x`);
    const link = (r) => this.resLinkHtml(r);
    const title = '<h2 class="text-lg font-bold">All posts</h2>';
    if (isPhone()) {
      const chips = [['views', 'Views'], ['posted_at', 'Newest'], ['score', 'Score'], ['likes', 'Likes']];
      return `<section class="flex flex-col gap-2">${title}
        <div class="flex gap-1 overflow-x-auto">${chips.map(([k, l]) => `<button class="${BTN} h-9 shrink-0 rounded-full border px-3 ${R.sort === k ? 'border-primary bg-accent text-accent-foreground' : 'border-border bg-background'}" data-act="resSort" data-k="${k}" aria-pressed="${R.sort === k}">${l}</button>`).join('')}</div>
        <div class="flex flex-col divide-y divide-border rounded-lg border border-border bg-background">${rows.map((r) => `
          <div class="flex items-center gap-3 p-3" data-act="slot" data-p="${r.post_id}" data-k="${r.platform}" role="button">
            <div class="min-w-0 flex-1"><div class="truncate text-sm font-semibold">${esc(r.title)}</div>
              <div class="flex items-center gap-1.5 text-xs text-muted-foreground">${dot(r.platform)}${LABEL[r.platform]} · ${fmtDay(new Date(r.posted_at))}${r.score != null ? ` · ${score(r)}` : ''}</div></div>
            <div class="text-right"><div class="text-sm font-bold tabular-nums">${views(r)}</div>${r.likes != null || r.comments != null ? `<div class="whitespace-nowrap text-xs text-muted-foreground tabular-nums">${[r.likes != null ? nWord(r.likes, 'like') : '', r.comments != null ? nWord(r.comments, 'comment') : ''].filter(Boolean).join(' · ')}</div>` : ''}</div>
            ${link(r)}</div>`).join('')}</div></section>`;
    }
    const num = new Set(['views', 'likes', 'comments', 'score']);
    return `<section class="flex flex-col gap-2">${title}<div class="overflow-x-auto rounded-lg border border-border bg-background"><table class="w-full text-sm">
      <thead class="bg-secondary text-xs text-muted-foreground"><tr>${RES_COLS.map(([k, l]) => `<th class="p-0 ${num.has(k) ? 'text-right' : 'text-left'}" ${R.sort === k ? `aria-sort="${R.dir > 0 ? 'ascending' : 'descending'}"` : ''}><button class="inline-flex w-full items-center gap-1 px-2 py-2 font-semibold hover:text-foreground ${num.has(k) ? 'justify-end' : ''} ${R.sort === k ? 'text-foreground' : ''}" data-act="resSort" data-k="${k}">${l}${R.sort === k ? (R.dir > 0 ? ' ↑' : ' ↓') : ''}</button></th>`).join('')}<th class="w-12"></th></tr></thead>
      <tbody class="divide-y divide-border">${rows.map((r) => `
        <tr class="cursor-pointer hover:bg-accent/40" data-act="slot" data-p="${r.post_id}" data-k="${r.platform}">
          <td class="max-w-[320px] truncate p-2 font-semibold">${esc(r.title)}</td>
          <td class="whitespace-nowrap p-2"><span class="inline-flex items-center gap-1.5">${dot(r.platform)}${LABEL[r.platform]}</span></td>
          <td class="whitespace-nowrap p-2 text-muted-foreground">${fmtDay(new Date(r.posted_at))}</td>
          <td class="p-2 text-right font-semibold tabular-nums">${views(r)}</td>
          <td class="p-2 text-right tabular-nums">${fmtN(r.likes)}</td>
          <td class="p-2 text-right tabular-nums">${fmtN(r.comments)}</td>
          <td class="p-2 text-right tabular-nums text-muted-foreground">${score(r)}</td>
          <td class="p-1">${link(r)}</td>
        </tr>`).join('')}</tbody></table></div></section>`;
  }

  resultsClick(d, run) {
    const R = this.resState();
    switch (d.act) {
      case 'resLink': return; // the link opens itself; don't open the row
      case 'resDays': R.days = +d.k; R.error = null; this.renderResults(); return;
      case 'resRetry': R.error = null; this.renderResults(); return;
      case 'resSort': R.dir = R.sort === d.k ? -R.dir : (['title', 'platform'].includes(d.k) ? 1 : -1); R.sort = d.k; this.renderResults(); return;
      case 'resRefresh': run(async () => {
        R.refreshing = true; this.renderResults();
        try {
          const { collected_at: before } = await this.api('POST', '/results/refresh');
          const d = await this.resWaitForNumbers(before);
          const errs = d?.collect_errors || [];
          this.toast(!d ? 'Still refreshing. Check back in a minute' : errs.length ? `Updated · ${errs[0]}` : 'Numbers updated');
        } finally {
          R.refreshing = false;
          if (this.route() === 'results') this.renderResults();
        }
      }); return;
      case 'resAgain': run(async () => {
        const it = this.item(+d.item);
        if (!window.confirm(`Schedule "${it?.title || 'this video'}" again? It goes into the plan by your rule.`)) return;
        await this.api('POST', '/schedule', { item_ids: [+d.item] });
        await this.load();
        this.toast('Added to the plan', { label: 'See in Plan', fn: () => { window.location.hash = 'plan'; } });
      }); return;
      default:
    }
  }

  // Refresh runs in the background: ask for the results every 3 s, up to a
  // minute, until collected_at moves. The fresh results, or null on timeout.
  async resWaitForNumbers(before) {
    const R = this.resState();
    for (let i = 0; i < 20; i += 1) {
      await sleep(3000);
      const days = R.days;
      const d = await this.api('GET', `/results?days=${days}`);
      if (days === R.days) { R.data = d; R.at = Date.now(); }
      if (d.collected_at && d.collected_at !== before) return d;
    }
    return null;
  }

  // TikTok has no API: a Views box on posted TikToks in the slot sheet.
  slotViewsHtml(p, s) {
    if (s.platform !== 'tiktok' || s.state !== 'posted') return '';
    const st = this.resState().slotStats[`${p.id}|tiktok`];
    if (st === undefined) this.loadSlotStats(p.id);
    return `<label class="flex flex-col gap-1 text-sm">Views<input type="text" inputmode="numeric" autocomplete="off" class="${INPUT}" data-in="slotViews" value="${st?.views ?? ''}" placeholder="From the TikTok app" aria-label="TikTok views"></label>
      <p class="-mt-2 text-xs text-muted-foreground" data-views-when>${this.viewsWhen(st)}</p>`;
  }

  viewsWhen(st) {
    return st ? `Saved ${dayWord(new Date(`${st.captured_on}T12:00:00`))}` : '';
  }

  async loadSlotStats(pid) {
    const R = this.resState();
    R.slotStats[`${pid}|tiktok`] = null; // asked once per page load
    try {
      const st = (await this.api('GET', `/posts/${pid}/platforms/tiktok/stats`)).stats[0] || null;
      R.slotStats[`${pid}|tiktok`] = st;
      const [open] = this.ui.slot || [];
      const input = this.layerTarget.querySelector('[data-in="slotViews"]');
      if (open !== pid || !st || !input) return;
      if (!input.value && document.activeElement !== input) input.value = st.views ?? '';
      const when = this.layerTarget.querySelector('[data-views-when]');
      if (when) when.textContent = this.viewsWhen(st);
    } catch (_) { /* the box still works without the old number */ }
  }

  async saveSlotViews(value) {
    const [pid, k] = this.ui.slot || [];
    const views = String(value).replace(/[\s,._]/g, '');
    if (!pid || !views) return;
    if (!/^\d+$/.test(views)) { this.toast('Views must be a number'); return; }
    try {
      const { stats } = await this.api('POST', `/posts/${pid}/platforms/${k}/stats`, { views });
      const R = this.resState();
      R.slotStats[`${pid}|${k}`] = stats[0] || null;
      R.at = 0;
      const when = this.layerTarget.querySelector('[data-views-when]');
      if (when) when.textContent = this.viewsWhen(stats[0]);
      this.toast(`${Number(views).toLocaleString()} views saved`);
      if (this.route() === 'results') this.loadResults();
    } catch (err) { this.toast(err.message); }
  }

  // ---------- series: Library select + item sheet → Series sheet ----------
  // Kept out of the existing methods: select mode draws over renderLibrary's
  // output, and the sheet listens for its own data-act="series…" clicks and
  // data-series-in fields (the shared handlers ignore both).
  seriesInit() {
    Object.assign(this.ui, { picking: false, picked: [], series: null });
    const draw = this.constructor.prototype.renderLibrary;
    this.renderLibrary = (...args) => { draw.apply(this, args); this.seriesLibrary(); };
    if (this.seriesBound) return;
    this.seriesBound = true;
    this.element.addEventListener('click', (e) => this.seriesClick(e));
    ['input', 'change'].forEach((type) => this.element.addEventListener(type, (e) => this.seriesField(e)));
  }

  // Select toggle, a numbered checkbox per row (the number is the series
  // order), and the "Schedule series" bar.
  seriesLibrary() {
    const rows = this.viewTarget.querySelector('[data-lib-rows]');
    const head = this.viewTarget.querySelector('[data-lib-kinds]')?.parentElement;
    if (!rows || !head) return;
    this.ui.picked = this.ui.picked.filter((id) => this.item(id));
    const on = this.ui.picking;
    head.querySelector('[data-series-toggle]')?.remove();
    head.insertAdjacentHTML('beforeend', `<button class="${BTN} h-9 shrink-0 self-end rounded-full border px-3 sm:self-auto ${on ? 'border-primary bg-accent text-accent-foreground' : 'border-border bg-background'}" data-act="seriesSelect" data-series-toggle aria-pressed="${on}">${on ? 'Cancel' : 'Select'}</button>`);
    if (on) {
      rows.querySelector('thead tr')?.insertAdjacentHTML('afterbegin', '<th class="w-10 p-2"></th>');
      rows.querySelectorAll('[data-act="openItem"][data-item]').forEach((row) => {
        const n = this.ui.picked.indexOf(+row.dataset.item) + 1;
        const box = `<span class="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 text-xs font-bold ${n ? 'border-primary bg-primary text-primary-foreground' : 'border-input bg-background'}" role="checkbox" aria-checked="${n > 0}" aria-label="Select">${n || ''}</span>`;
        row.dataset.act = 'seriesPick';
        row.insertAdjacentHTML('afterbegin', row.tagName === 'TR' ? `<td class="p-2">${box}</td>` : box);
      });
    }
    this.viewTarget.querySelector('[data-series-bar]')?.remove();
    const n = this.ui.picked.length;
    if (on && n) {
      this.viewTarget.insertAdjacentHTML('beforeend', `<div class="fixed inset-x-0 bottom-0 z-20 px-3 pb-[max(12px,env(safe-area-inset-bottom))]" data-series-bar><div class="mx-auto max-w-[520px]">
        <button class="${BTN_PRIMARY} h-14 w-full rounded-xl text-lg shadow-lg" data-act="seriesOpen">${I_SERIES}Schedule series (${n})</button></div></div>`);
    }
  }

  seriesItemBtn(it) {
    return `<button class="${BTN_OUTLINE} w-full" data-act="seriesOpenItem" data-item="${it.id}">${I_SERIES}Schedule as series</button>`;
  }

  // Several items: one post each, in the order picked. One item: the same
  // video on every date, or each of its versions in turn.
  seriesOpen(ids) {
    const items = ids.map((id) => this.item(id)).filter(Boolean);
    if (!items.length) return;
    const versions = items.length === 1 ? items[0].files.filter((f) => f.version).length : 0;
    const mode = items.length > 1 ? 'list' : versions > 1 ? 'versions' : 'repeat';
    const plats = SERIES_PL.filter((k) => this.data_.rule.platforms.includes(k));
    const now = new Date();
    this.ui.series = {
      ids: items.map((it) => it.id), mode, pattern: 'd1', weekdays: [1, 3, 5], weekday: 5,
      start: 'today', startDate: ymd(addDays(now, 1)), end: mode === 'repeat' ? 'count' : 'none', count: '5', endDate: `${now.getFullYear()}-12-31`,
      time: 'slots', at: '18:00', zone: 'local', plats: plats.length ? plats : ['youtube', 'facebook'],
      res: null, err: null, loading: true, saving: false, seq: 0,
    };
    this.openLayer('series');
    this.seriesPreview();
  }

  seriesClick(e) {
    const t = e.target.closest('[data-act]');
    if (!t || !t.dataset.act.startsWith('series') || !this.element.contains(t)) return;
    const { act, v } = t.dataset;
    switch (act) {
      case 'seriesSelect': this.ui.picking = !this.ui.picking; this.ui.picked = []; this.renderLibrary(); return;
      case 'seriesPick': { const id = +t.dataset.item; const p = this.ui.picked; this.ui.picked = p.includes(id) ? p.filter((x) => x !== id) : [...p, id]; this.renderLibrary(); return; }
      case 'seriesOpen': this.seriesOpen(this.ui.picked); return;
      case 'seriesOpenItem': this.seriesOpen([+t.dataset.item]); return;
      case 'seriesGo': this.seriesGo(); return;
      default:
    }
    const S = this.ui.series;
    if (!S) return;
    if (act === 'seriesMode') {
      S.mode = v;
      if (v === 'repeat' && S.end === 'none') S.end = 'count';
      if (v === 'versions' && S.end === 'count') S.end = 'none';
    } else if (act === 'seriesPattern') {
      S.pattern = v;
      if (v === 'weekly') { S.time = 'exact'; if (S.mode === 'repeat') S.end = 'date'; } // a countdown: to the end of the year
      if (v === 'twice') S.time = 'slots';
    } else if (act === 'seriesDay') {
      const w = +v;
      if (S.pattern === 'weekly') S.weekday = w;
      else if (!S.weekdays.includes(w)) S.weekdays = [...S.weekdays, w];
      else if (S.weekdays.length > 1) S.weekdays = S.weekdays.filter((x) => x !== w);
    } else if (act === 'seriesPlat') {
      if (S.plats.includes(v) && S.plats.length === 1) { this.toast('Keep one platform'); return; }
      S.plats = S.plats.includes(v) ? S.plats.filter((k) => k !== v) : SERIES_PL.filter((k) => S.plats.includes(k) || k === v);
    } else {
      const key = { seriesStart: 'start', seriesEnd: 'end', seriesTime: 'time', seriesZone: 'zone' }[act];
      if (!key) return;
      S[key] = v;
    }
    this.seriesRender();
    this.seriesPreview();
  }

  seriesField(e) {
    const key = e.target.dataset?.seriesIn;
    const S = this.ui.series;
    if (!key || !S) return;
    S[key] = e.target.value;
    this.seriesPreview();
  }

  seriesBody() {
    const S = this.ui.series;
    const [pattern, every] = { d1: ['every', 1], d2: ['every', 2], d3: ['every', 3], weekly: ['weekly_exact'], days: ['weekdays'], twice: ['per_day'] }[S.pattern];
    const body = {
      item_ids: S.ids, platforms: S.plats, pattern, repeat: S.mode === 'repeat', versions: S.mode === 'versions',
      start_date: S.start === 'pick' ? S.startDate : S.start, time: S.time === 'exact' ? S.at : 'slots', zone: S.zone,
    };
    if (every) body.every_days = every;
    if (pattern === 'weekdays') body.weekdays = S.weekdays.map((w) => WDAY[w]);
    if (pattern === 'weekly_exact') body.weekdays = [WDAY[S.weekday]];
    if (pattern === 'per_day') body.per_day = 2;
    if (S.end === 'count') body.count = S.count;
    if (S.end === 'date') body.end_date = S.endDate;
    return body;
  }

  // Debounced; only the latest answer is drawn.
  seriesPreview() {
    const S = this.ui.series;
    if (!S) return;
    S.loading = true;
    S.seq += 1;
    const seq = S.seq;
    this.seriesRefresh();
    clearTimeout(this.seriesTimer);
    this.seriesTimer = setTimeout(async () => {
      let res = null;
      let err = null;
      try { res = await this.api('POST', '/series/preview', this.seriesBody()); } catch (x) { err = x.message; }
      if (this.ui.series !== S || seq !== S.seq) return;
      Object.assign(S, { res, err, loading: false });
      this.seriesRefresh();
    }, 250);
  }

  async seriesGo() {
    const S = this.ui.series;
    if (!S || S.saving || S.loading) return;
    S.saving = true;
    this.seriesRefresh();
    try {
      const res = await this.api('POST', '/series', this.seriesBody());
      Object.assign(this.ui, { series: null, picking: false, picked: [] });
      this.closeLayer();
      await this.load();
      this.toast(`${res.count} scheduled${res.shifted ? ` · ${res.shifted} moved` : ''}`, { label: 'See in Plan', fn: () => { window.location.hash = 'plan'; } });
    } catch (err) {
      Object.assign(S, { saving: false, err: err.message, res: null });
      this.seriesRefresh();
    }
  }

  // Option taps redraw the sheet; keep its scroll where it was.
  seriesRender() {
    const y = this.layerTarget.querySelector('[data-series-scroll]')?.scrollTop || 0;
    this.renderLayer();
    const box = this.layerTarget.querySelector('[data-series-scroll]');
    if (box) box.scrollTop = y;
  }

  // Preview answers redraw only the list and the button, so typing keeps focus.
  seriesRefresh() {
    if (this.ui.layer !== 'series' || !this.ui.series) return;
    const list = this.layerTarget.querySelector('[data-series-preview]');
    const go = this.layerTarget.querySelector('[data-series-go]');
    if (list) list.innerHTML = this.seriesPreviewHtml();
    if (go) go.innerHTML = this.seriesGoHtml();
  }

  seriesHtml(top) {
    const S = this.ui.series;
    const items = (S?.ids || []).map((id) => this.item(id)).filter(Boolean);
    if (!items.length) return top('Gone');
    const seg = (act, opts, cur, cols) => `<div class="grid gap-1 rounded-lg bg-secondary p-1 ${cols}">${opts.map(([v, label, off]) => `<button class="rounded-md px-2 py-2 text-sm font-semibold disabled:opacity-40 ${cur === v ? 'bg-background shadow-sm' : 'text-muted-foreground'}" data-act="${act}" data-v="${v}" aria-pressed="${cur === v}" ${off ? 'disabled' : ''}>${label}</button>`).join('')}</div>`;
    const sec = (title, inner) => `<section class="flex flex-col gap-2"><h3 class="text-sm font-semibold text-muted-foreground">${title}</h3>${inner}</section>`;
    const one = items.length === 1 ? items[0] : null;
    const versions = one ? one.files.filter((f) => f.version).length : 0;
    const weekly = S.pattern === 'weekly';
    const twice = S.pattern === 'twice';
    const repeat = S.mode === 'repeat';

    const what = one
      ? `<div class="flex items-center gap-3"><div class="w-10 shrink-0">${this.thumb(one, 'w-10', '9/16')}</div><b class="min-w-0 flex-1 truncate">${esc(one.title)}</b></div>
        ${versions > 1 ? seg('seriesMode', [['versions', `Each version (${versions})`], ['repeat', 'Same video']], S.mode, 'grid-cols-2') : ''}`
      : `<ol class="flex flex-col gap-1 text-sm">${items.slice(0, 4).map((it, i) => `<li class="flex gap-2"><span class="w-5 shrink-0 text-right text-muted-foreground">${i + 1}</span><span class="min-w-0 truncate">${esc(it.title)}</span></li>`).join('')}${items.length > 4 ? `<li class="pl-7 text-muted-foreground">+${items.length - 4} more</li>` : ''}</ol>`;
    const picked = weekly ? [S.weekday] : S.weekdays;
    const days = `<div class="grid grid-cols-7 gap-1">${[1, 2, 3, 4, 5, 6, 0].map((w) => `<button class="${BTN} h-9 rounded-md border text-xs ${picked.includes(w) ? 'border-primary bg-accent text-accent-foreground' : 'border-border'}" data-act="seriesDay" data-v="${w}" aria-pressed="${picked.includes(w)}">${DAYS[w]}</button>`).join('')}</div>`;
    const ends = repeat ? [['count', 'Count'], ['date', 'End date']] : [['none', 'All'], ['count', 'Count'], ['date', 'End date']];
    const small = 'w-24 rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring';

    return `${top('Series')}<div class="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-4" data-series-scroll>
      ${sec(one ? 'Video' : `${items.length} videos, in this order`, what)}
      ${sec('Pattern', `${seg('seriesPattern', SERIES_PATTERNS, S.pattern, 'grid-cols-3')}${weekly || S.pattern === 'days' ? days : ''}`)}
      ${sec('Start', `${seg('seriesStart', [['today', 'Today'], ['after_queue', 'After the queue'], ['pick', 'Pick a day']], S.start, 'grid-cols-3')}
        ${S.start === 'pick' ? `<input type="date" class="${INPUT}" data-series-in="startDate" value="${esc(S.startDate)}" min="${ymd(new Date())}" aria-label="Start day">` : ''}`)}
      ${sec('End', `${seg('seriesEnd', ends, S.end, repeat ? 'grid-cols-2' : 'grid-cols-3')}
        ${S.end === 'count' ? `<label class="flex items-center gap-2 text-sm"><input type="number" min="1" max="400" inputmode="numeric" class="${small}" data-series-in="count" value="${esc(S.count)}" aria-label="Posts">posts</label>` : ''}
        ${S.end === 'date' ? `<input type="date" class="${INPUT}" data-series-in="endDate" value="${esc(S.endDate)}" min="${ymd(new Date())}" aria-label="End day">` : ''}`)}
      ${sec('Time', `${seg('seriesTime', [['slots', 'Usual slots', weekly], ['exact', 'Exact time', twice]], S.time, 'grid-cols-2')}
        ${S.time === 'exact' ? `<div class="grid grid-cols-[1fr_auto] gap-2"><input type="time" class="${INPUT}" data-series-in="at" value="${esc(S.at)}" aria-label="Time">${seg('seriesZone', [['local', 'Local'], ['et', 'ET']], S.zone, 'grid-cols-2')}</div>` : '<p class="text-xs text-muted-foreground">The usual Eastern slots (YouTube 4 a day, Facebook 2); a full day moves to the next free slot</p>'}`)}
      ${sec('Platforms', `<div class="flex flex-wrap gap-2">${SERIES_PL.map((k) => `<button class="${BTN} h-10 rounded-full border px-3 ${S.plats.includes(k) ? 'border-primary bg-accent text-accent-foreground' : 'border-border'}" data-act="seriesPlat" data-v="${k}" aria-pressed="${S.plats.includes(k)}">${dot(k)}${LABEL[k]}</button>`).join('')}</div>`)}
      ${sec('Preview', `<div data-series-preview>${this.seriesPreviewHtml()}</div>`)}
    </div><div class="border-t border-border p-3 pb-[max(12px,env(safe-area-inset-bottom))]" data-series-go>${this.seriesGoHtml()}</div>`;
  }

  seriesPreviewHtml() {
    const S = this.ui.series;
    if (S.err) return `<p class="rounded-md bg-destructive/10 p-3 text-sm text-destructive">${esc(S.err)}</p>`;
    if (!S.res) return '<p class="text-sm text-muted-foreground">Working it out…</p>';
    const posts = [];
    S.res.plan.forEach((r) => { (posts[r.n - 1] = posts[r.n - 1] || []).push(r); });
    const titles = new Set(S.res.plan.map((r) => r.item_id)).size > 1;
    const versions = new Set(S.res.plan.map((r) => r.version)).size > 1;
    const left = S.res.left_over || [];
    return `<div class="flex flex-col gap-2 transition-opacity ${S.loading ? 'opacity-50' : ''}">
      <p class="text-sm"><b>${posts.length} ${posts.length === 1 ? 'post' : 'posts'}</b>${S.res.shifted ? ` · <span class="font-semibold text-amber-600">${S.res.shifted} moved to the next free slot</span>` : ''}</p>
      <div class="flex flex-col divide-y divide-border rounded-lg border border-border">${posts.map((rows) => this.seriesRowHtml(rows, titles, versions)).join('')}</div>
      ${left.length ? `<div class="flex flex-col gap-1 text-sm"><p class="text-muted-foreground">Left out:</p>
        <ul class="flex flex-col gap-0.5">${left.map((x) => `<li class="flex items-baseline gap-2"><span class="min-w-0 truncate">${esc(x.title)}${x.version ? ` v${x.version}` : ''}</span><span class="shrink-0 text-xs text-muted-foreground">${esc(SERIES_WHY[x.reason] || x.reason || '')}</span></li>`).join('')}</ul></div>` : ''}</div>`;
  }

  // One post: its day (Andrew's time), then each platform's time or queue place.
  seriesRowHtml(rows, titles, versions) {
    const first = rows.find((r) => r.at);
    const day = first ? new Date(first.at) : null;
    const r0 = rows[0];
    const name = `${titles ? esc(r0.title) : ''}${versions && r0.version ? ` <span class="text-muted-foreground">v${r0.version}</span>` : ''}`;
    const chips = rows.map((r) => {
      if (!r.at) return `<span class="inline-flex items-center gap-1">${dot(r.platform)}${LABEL[r.platform]} #${r.queue}</span>`;
      const t = new Date(r.at);
      const [y, m, d] = (r.wanted || '').split('-').map(Number);
      const why = r.shifted && y ? `${this.seriesDay(new Date(y, m - 1, d))} was full` : '';
      return `<span class="inline-flex items-center gap-1 ${r.shifted ? 'font-semibold text-amber-600' : ''}" ${why ? `title="${why}"` : ''}>${dot(r.platform)}${LABEL[r.platform]} ${offOf(t) !== offOf(day) ? `${this.seriesDay(t)} ` : ''}${fmtT(t)}${r.shifted ? ' · moved' : ''}</span>`;
    }).join('');
    return `<div class="flex flex-col gap-1 px-3 py-2"><div class="flex items-baseline gap-2 text-sm"><span class="shrink-0 font-semibold">${day ? this.seriesDay(day) : 'Queue'}</span><span class="min-w-0 truncate">${name}</span></div>
      <div class="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">${chips}</div></div>`;
  }

  seriesGoHtml() {
    const S = this.ui.series;
    const n = S.res && !S.err ? S.res.count : 0;
    const label = S.saving ? 'Scheduling…' : n ? `Schedule ${n} ${n === 1 ? 'post' : 'posts'}` : 'Schedule';
    return `<button class="${BTN_PRIMARY} h-12 w-full text-base" data-act="seriesGo" ${!n || S.loading || S.saving ? 'disabled' : ''}>${I.ok}${label}</button>`;
  }

  seriesDay(d) {
    const n = offOf(d);
    if (n === 0) return 'Today';
    if (n === 1) return 'Tomorrow';
    return `${DAYS[d.getDay()]} ${MONTHS[d.getMonth()]} ${d.getDate()}`;
  }

  // ---------- booked on a platform: ask before changing it there ----------
  // Like api(), but a 409 "booked" answer (it's already in Facebook's or
  // YouTube's own scheduler) opens a sheet asking whether the platform should
  // follow, then repeats the call with that choice. null = cancelled.
  async apiAsk(method, path, body, kind) {
    const res = await fetch(this.apiValue + path, {
      method,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': this.csrf },
      credentials: 'same-origin',
      body: body ? JSON.stringify(body) : undefined,
    });
    const json = await res.json().catch(() => ({}));
    if (res.status === 409 && json.error === 'booked') {
      const choice = await this.askBooked(json.booked || [], kind);
      if (!choice) return null;
      const r = await this.api(method, `${path}${path.includes('?') ? '&' : '?'}unschedule=${choice}`, body);
      if (choice === 'true') this.cancelsAt = 0; // a hand-off may have joined the list
      return r;
    }
    if (!res.ok) throw new Error(json.message || json.error || `HTTP ${res.status}`);
    return json;
  }

  askBooked(booked, kind) {
    return new Promise((resolve) => {
      this.ui.booked = { booked, kind, back: this.ui.layer === 'booked' ? null : this.ui.layer, resolve };
      this.openLayer('booked');
    });
  }

  // Back to the sheet it came from (slot / item), then carry on.
  bookedPick(v) {
    const B = this.ui.booked;
    if (!B) return;
    this.ui.booked = null;
    if (B.back) this.openLayer(B.back); else this.closeLayer();
    B.resolve(v || null);
  }

  // kind: 'move' (new time or platform), 'plan' (back to Planned), 'delete'.
  // Planned again can't change only the DAM (it would be booked twice), so
  // its other choice is Keep it Scheduled, which just closes the sheet.
  bookedHtml() {
    const B = this.ui.booked;
    if (!B) return '';
    const move = B.kind === 'move';
    const plan = B.kind === 'plan';
    const at = (s) => { const d = s.scheduled_for && new Date(s.scheduled_for); return d ? `${DAYS[d.getDay()]} ${fmtT(d)}` : 'Booked'; };
    const byClaude = [...new Set(B.booked.filter((s) => s.via === 'claude' && s.platform !== 'youtube').map((s) => LABEL[s.platform]))];
    const yt = B.booked.some((s) => s.via === 'claude' && s.platform === 'youtube');
    const others = plan
      ? `<button class="${BTN_OUTLINE}" data-act="bookedPick" data-v="">Keep it Scheduled</button>`
      : `<button class="${BTN_OUTLINE}" data-act="bookedPick" data-v="false">${move ? 'Only change the DAM' : 'Only remove from the DAM'}</button>
        <button class="${BTN_GHOST}" data-act="bookedPick" data-v="">Cancel</button>`;
    return `<div class="flex items-center gap-2 border-b border-border px-4 py-3"><h2 class="min-w-0 flex-1 truncate text-lg font-bold">Already booked</h2><button class="${BTN_ICON}" data-act="bookedPick" data-v="" aria-label="Cancel">${I.x}</button></div>
      <div class="flex flex-col gap-3 overflow-y-auto p-4">
        <p class="text-sm text-muted-foreground">${move ? 'Booked there at the old time.' : 'It still goes out unless it\'s unscheduled there.'}</p>
        <div class="flex flex-col divide-y divide-border rounded-lg border border-border">${B.booked.map((s) => `<div class="flex items-center gap-2 px-3 py-2 text-sm">${dot(s.platform)}<span class="flex-1 font-semibold">${LABEL[s.platform]} · ${at(s)}</span>${/^https?:\/\//.test(s.url || '') ? `<a class="text-xs text-muted-foreground underline hover:text-foreground" href="${esc(s.url)}" target="_blank" rel="noopener">Open</a>` : ''}${studioLink(s.studio_url)}</div>`).join('')}</div>
        ${yt ? `<p class="text-sm font-semibold">${YT_WAIT}</p>` : ''}
        ${byClaude.length ? `<p class="text-xs text-muted-foreground">${byClaude.join(', ')}: Claude ${move ? 'unschedules it and books the new time' : 'unschedules it'} on its next daily run.</p>` : ''}
        <button class="${BTN_PRIMARY} h-12 text-base" data-act="bookedPick" data-v="true">${move ? 'Move it there too' : 'Unschedule there too'}</button>
        ${others}
      </div>`;
  }

  // YouTube hand-offs are left to toastAfter.
  unschedText(r) {
    const say = (u) => ({ handed_off: `${u.label}: Claude unschedules it next run`, already_live: `Already live on ${u.label}, left it there`, moved: `${u.label} moved` }[u.done] || `${u.label} unscheduled`);
    return (r?.unscheduled || []).filter((u) => !this.ytHandOff(u)).map((u) => ` · ${say(u)}`).join('');
  }

  ytHandOff(u) { return u.done === 'handed_off' && u.platform === 'youtube'; }

  // After a change that may have handed a YouTube booking to Claude: say it
  // waits for 9 AM, with Studio one tap away.
  toastAfter(r, msg) {
    const u = (r?.unscheduled || []).find((x) => this.ytHandOff(x));
    if (!u) { this.toast(msg); return; }
    this.toast(`${msg} · ${YT_WAIT}`, u.studio_url ? { label: 'Studio', href: u.studio_url } : null);
  }

  // A move that found it already out: it stayed there and is Posted now.
  liveText(r) {
    const u = (r?.unscheduled || []).find((x) => x.done === 'already_live');
    return u ? `Already live on ${u.label}, left it there` : '';
  }

  // Menu: what Claude still has to unschedule by hand (YouTube has no API).
  cancelsHtml() {
    if (!this.cancelsAt || Date.now() - this.cancelsAt > 30000) this.loadCancels();
    const by = {};
    (this.cancels || []).forEach((c) => { (by[c.platform] = by[c.platform] || []).push(c); });
    return Object.entries(by).map(([k, cs]) => `<section class="flex flex-col gap-1 text-sm">
      <h3 class="flex items-center gap-2 font-semibold">${dot(k)}${LABEL[k] || esc(k)} to unschedule: ${cs.length}</h3>
      ${cs.map((c) => `<div class="flex items-center gap-2"><a class="min-w-0 flex-1 truncate text-muted-foreground underline hover:text-foreground" href="${esc(/^https?:\/\//.test(c.url || '') ? c.url : '#')}" target="_blank" rel="noopener">${esc(c.title || c.url)}</a>${studioLink(c.studio_url)}</div>`).join('')}</section>`).join('');
  }

  async loadCancels() {
    this.cancelsAt = Date.now();
    try { this.cancels = (await this.api('GET', '/cancellations')).cancellations; } catch (_) { return; }
    if (this.ui.layer === 'menu') this.renderLayer();
  }

  // ---------- bits ----------
  // Feedback on the button that was pressed (a toast can sit behind an open sheet).
  flash(btn, msg) {
    if (!btn.dataset.label) btn.dataset.label = btn.innerHTML;
    const ok = msg === 'Copied';
    btn.innerHTML = `${ok ? I.ok : ''}${msg}`;
    btn.classList.toggle('text-primary', ok);
    btn.classList.toggle('text-destructive', !ok);
    clearTimeout(btn.flashTimer);
    btn.flashTimer = setTimeout(() => { btn.innerHTML = btn.dataset.label; delete btn.dataset.label; btn.classList.remove('text-primary', 'text-destructive'); }, 1600);
  }

  copy(text) {
    if (navigator.clipboard && window.isSecureContext) { navigator.clipboard.writeText(text).catch(() => {}); return; }
    const a = document.createElement('textarea');
    a.value = text; a.style.position = 'fixed'; a.style.opacity = '0';
    document.body.appendChild(a); a.select();
    try { document.execCommand('copy'); } catch (_) { /* ignore */ }
    a.remove();
  }

  toast(msg, action = null, sticky = false) {
    const el = this.toastTarget;
    // action: { label, fn } for a button, or { label, href } for a link (opens a new tab, stays up longer).
    const act = !action ? '' : action.href
      ? `<a class="font-semibold text-primary-foreground underline" href="${esc(action.href)}" target="_blank" rel="noopener" data-toast-act>${esc(action.label)}</a>`
      : `<button class="font-semibold text-primary-foreground underline" data-toast-act>${esc(action.label)}</button>`;
    el.innerHTML = `<div class="flex max-w-md items-center gap-3 rounded-xl bg-foreground px-4 py-3 text-sm text-background shadow-lg"><span class="flex-1">${esc(msg)}</span>${act}</div>`;
    el.classList.remove('hidden'); el.classList.add('flex');
    const btn = el.querySelector('[data-toast-act]');
    if (btn) btn.addEventListener('click', () => { el.classList.add('hidden'); if (action.fn) action.fn(); });
    clearTimeout(this.toastTimer);
    if (!sticky) this.toastTimer = setTimeout(() => { el.classList.add('hidden'); el.classList.remove('flex'); }, action?.href ? 12000 : action ? 6000 : 2500);
  }
}
